<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { usePresetProgress } from '@/features/startTask/preset_task/shared/preset/composables/usePresetProgress';
import { Icon } from '@iconify/vue';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';
import type { OfficerDetail, OfficerRoleType, SignerEntry } from '@/features/AI-documents/types/docGen.types';
import { buildAGMVariables, detectAGMDocFlags } from '../mappers/agmMapper';
import type { RetiringDirectorInput, MemberInput } from '../mappers/agmMapper';
import { getTaskConfigByCode } from '@/features/startTask/api/taskConfigApi';
import { docGenApi } from '@/features/AI-documents/api/docGenApi';
import { useDateField } from '@/shared/composables/dateUtils';
import SignerListField from '@/features/AI-documents/components/SignerListField.vue';

interface PersonLookupContext {
  fieldGroup: 'retiringDirector' | 'member' | 'lodger' | 'statutoryDeclarant' | 'certifyingSecretary' | 'auditor' | 'auditorAddress';
  index?: number;
  defaultRole: OfficerRoleType | null;
}

const props = defineProps<{
  selection: PreSetSelection;
  entityData: Record<string, any> | null;
  taskTitle: string;
  isReady: boolean;
  currentStep: string;
  /** 챗봇에서 선택된 사람의 OfficerDetail (자동 채움용) */
  selectedPerson?: OfficerDetail | null;
  /** 마지막으로 요청한 lookup 컨텍스트 (어떤 필드를 채워야 하는지) */
  activePersonContext?: PersonLookupContext | null;
  /** 챗봇 address chooser 결과 */
  selectedAddress?: { value: string; type: string; position?: string } | null;
  /** address chooser 컨텍스트 (어떤 member 의 address 를 채울지) */
  activeAddressContext?: { fieldGroup: 'member'; index: number } | null;
  /** Edit 모드 — 값이 있으면 기존 task 수정(Update). 호스트가 restoreState 로 입력 복원. */
  editTaskId?: number | null;
}>();

// Edit(수정) 모드 — 버튼 라벨 'Update Task' + create→update 분기.
const editMode = computed(() => !!props.editTaskId);

// restoreState 직후 입력 스냅샷(기준선). Update Task 는 실제 변경이 있을 때만 활성화.
const editBaseline = ref<string | null>(null);
const isDirty = computed(() => {
  if (!editMode.value) return true; // Create 모드는 항상 활성
  if (editBaseline.value == null) return false; // 기준선 캡처 전 — 비활성
  try { return JSON.stringify(gatherState()) !== editBaseline.value; } catch { return true; }
});

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'create-task', data: { taskTitle: string }): void;
  (e: 'task-created', data: { taskId: number; taskTitle: string }): void;
  (e: 'request-person-lookup', context: PersonLookupContext): void;
  (
    e: 'request-address-lookup',
    context: { fieldGroup: 'member'; index: number; addresses: any[]; subjectLabel?: string }
  ): void;
  (e: 'change-entity'): void;
  (e: 'change-task-title'): void;
}>();

const router = useRouter();

const creating = ref(false);
const progress = ref('');

// 진행 상태는 호스트(PreSetDocGenView)의 공통 중앙 오버레이로 흘려보낸다. (로컬 creating/progress 는 버튼 :loading·인라인 메시지용으로 유지)
const presetProgress = usePresetProgress();
watch(creating, (v) => { presetProgress.busy = v; });
watch(progress, (v) => { presetProgress.message = v; });

const taskConfigSteps = ref<string[]>([]);

// ─── Signer assignment (bulk vs per-document) ───
const signerAssignDialog = ref(false);
const signerAssignMode = ref<'bulk' | 'perDoc' | null>(null);
const bulkSigners = ref<SignerEntry[]>([]);
const showBulkSignerPanel = ref(false);

function onCreateTaskClick() {
  // TODO(temporary): signer assignment dialog disabled by request.
  // 기존: Bulk/Per-document 선택 모달 → bulk signer 적용 플로우
  // 현재: 모달 없이 바로 Task 생성 진행 (서명자 지정은 문서 생성 후 개별/후속 플로우에서 처리)
  signerAssignDialog.value = false;
  handleCreateTask();
}

function chooseSignerMode(mode: 'bulk' | 'perDoc') {
  signerAssignMode.value = mode;
  signerAssignDialog.value = false;
  if (mode === 'bulk') {
    showBulkSignerPanel.value = true;
    return;
  }
  handleCreateTask();
}

function onBulkSignerFieldUpdate(raw: string) {
  try {
    const parsed = JSON.parse(raw);
    bulkSigners.value = Array.isArray(parsed) ? (parsed as any) : [];
  } catch {
    bulkSigners.value = [];
  }
  showBulkSignerPanel.value = true;
  progress.value = bulkSigners.value.length ? `Selected ${bulkSigners.value.length} signer(s) for all documents.` : '';
}

// ─── Entity 정보 ───
const effectiveTaskTitle = computed(() => (props.taskTitle || '').trim());
const entityName = computed(() => {
  const name = props.entityData?.companyName || props.entityData?.entityName || '';
  const code = props.entityData?.companySuffix || '';
  const suffixMap: Record<string, string> = {
    M31001: 'SDN. BHD.', M31002: 'BHD.', M31003: 'LTD.', M31004: 'PTE. LTD.',
  };
  const suffix = suffixMap[code] || code;
  return suffix ? `${name} ${suffix}` : name;
});
const icpCd = computed(() => props.entityData?.icpCd || props.entityData?.entityCd || '');

// ─── 문서 기반 섹션 토글 ───
const docFlags = computed(() => detectAGMDocFlags(props.selection.documents || []));

type DocMeta = {
  docCode: string;
  templateName: string;
  flags: ReturnType<typeof detectAGMDocFlags>;
};

const docsMeta = computed<DocMeta[]>(() =>
  (props.selection.documents || []).map((d: any) => ({
    docCode: String(d?.docCode || '').trim(),
    templateName: String(d?.templateName || d?.label || '').trim(),
    flags: detectAGMDocFlags([d]),
  }))
);

function docLabel(d: DocMeta): string {
  return d.templateName || d.docCode || 'Document';
}

type SectionKey =
  | 'company'
  | 'dates'
  | 'resolution'
  | 'conditions'
  | 'retiringDirectors'
  | 'dividend'
  | 'remuneration'
  | 'members'
  | 'auditor'
  | 'lodger'
  | 'documents';

function docsForSection(key: SectionKey): DocMeta[] {
  const all = docsMeta.value;
  switch (key) {
    case 'company':
    case 'dates':
    case 'resolution':
    case 'conditions':
      // 공통 섹션: 선택된 모든 문서에 적용
      return all;
    case 'retiringDirectors':
      return all.filter(d => d.flags.hasAdoption);
    case 'dividend':
      return all.filter(d => d.flags.hasDividend);
    case 'remuneration':
      return all.filter(d => d.flags.hasRemuneration);
    case 'members':
      return all.filter(d => d.flags.hasCoverLetter || d.flags.hasFsReceipt);
    case 'auditor':
      // AGM_AUTOMATION 기준: audited 전용 (A2, A6) — 현재 화면에서는 audited 패키지에서만 노출
      return all.filter(d => d.flags.isAudited);
    case 'lodger':
      return all.filter(d => d.flags.hasAuditExemption);
    case 'documents':
      return all;
    default:
      return all;
  }
}

const MAX_DOC_CHIPS = 3;

/**
 * 문서가 hasDividend 등 사용자 플래그 때문에 생성에서 제외되는지 판정.
 * Documents 리스트에서 시각적 표시 + handleCreateTask 의 필터 기준으로 사용.
 */
function isDocSkipped(templateName: string): { skipped: boolean; reason?: string } {
  const name = String(templateName || '');
  if (!hasDividend.value && /declaration\s*of\s*dividend|dividend/i.test(name)) {
    return { skipped: true, reason: 'Has Dividend = No' };
  }
  return { skipped: false };
}

const skippedDocsCount = computed(() =>
  (props.selection.documents || []).filter((d: any) => isDocSkipped(d?.templateName || '').skipped).length
);

// ─── Entity 자동 조회 (regNo) ───
const regNo = ref('');
const regNoOld = ref('');   // 구 등록번호 (REG_NO_OLD) — 헤더 [[if old_registration_no]] 분기용
const articleNo = ref('');   // 정관 전체 텍스트 (참고용 표시)
const electionArticleNo = ref(''); // Re-election 근거 조항 (L_ENTITY.ELECTIONARTICLE_NO)
const electionArticleOptions = ref<string[]>([]);
const electionArticleSelected = ref<string>(''); // '' allowed
const entityInfoLoading = ref(false);

// tlinkData에 registrationNo가 이미 있으면 우선 반영
watch(
  () => props.entityData,
  (ed) => {
    if (!ed) return;
    if (!regNo.value && (ed.registrationNo || ed.registrationNumber)) {
      regNo.value = String(ed.registrationNo || ed.registrationNumber || '').trim();
    }
  },
  { immediate: true, deep: true }
);

// ─── Entity Profile (FYE 등) ───
const entityProfileLoading = ref(false);

function normalizeProfileDateToIso(input: any): string {
  const v = String(input || '').trim();
  if (!v) return '';

  // 1) Already ISO-ish: 2026-04-27 ...
  const isoMatch = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;

  // 2) entity_my/profile currently formats dates as en-GB "DD/MM/YYYY"
  const gbMatch = v.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (gbMatch) return `${gbMatch[3]}-${gbMatch[2]}-${gbMatch[1]}`;

  // 3) Fallback: try Date parsing (best-effort)
  try {
    const d = new Date(v);
    if (isNaN(d.getTime())) return '';
    const yyyy = String(d.getFullYear());
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  } catch {
    return '';
  }
}

async function loadEntityProfile(entityCd: string) {
  if (!entityCd) return;
  entityProfileLoading.value = true;
  try {
    // MY 우선 → 실패 시 SG fallback
    const tryFetch = async (url: string) => {
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) return null;
      const json = await res.json();
      return json?.data || json?.profile || json || null;
    };
    const my = await tryFetch(`/api/entity_my/profile/${encodeURIComponent(entityCd)}`);
    const profile = my || (await tryFetch(`/api/entity/profile/${encodeURIComponent(entityCd)}`));
    if (!profile) return;

    // Accounting Dates (entity_my/profile shape)
    const accounting = (profile as any)?.accountingDates || (profile as any)?.accounting_dates || null;

    // Registration No (fallback)
    if (!regNo.value) {
      regNo.value =
        profile.registrationNo ||
        profile.registrationNumber ||
        profile.regNo ||
        profile.REG_NO ||
        accounting?.registrationNo ||
        '';
    }

    // Re-election Regulation (ELECTIONARTICLE_NO + options)
    const resolvedElection =
      String(
        profile?.resolution?.electionArticleNo ||
          profile?.resolution?.electionArticleNoVal ||
          profile?.resolution?.ELECTIONARTICLE_NO ||
          profile?.electionArticleNo ||
          profile?.ELECTIONARTICLE_NO ||
          ''
      ).trim();
    const normalizedElection = /^nil$/i.test(resolvedElection) ? '' : resolvedElection;
    if (!electionArticleNo.value) electionArticleNo.value = normalizedElection;
    if (!electionArticleSelected.value) electionArticleSelected.value = electionArticleNo.value || '';

    const rawOpts = profile?.resolution?.electionArticleNoOptions || [];
    if (Array.isArray(rawOpts) && rawOpts.length) {
      const opts = rawOpts
        .map((o: any) => String(o?.value ?? o?.ArticleNo ?? o?.articleNo ?? o ?? '').trim())
        .filter(Boolean);
      electionArticleOptions.value = Array.from(new Set(opts));
    }

    // Financial Year End → 자동 채움 X, suggestion ref 에만 저장 (사용자가 수락 시 채움)
    {
      const fye =
        accounting?.financialYearEnd ||
        profile.financialYearEnd ||
        profile.fiscalYearEnd ||
        profile.FINANCIAL_YEAR_END ||
        '';
      if (typeof fye === 'string' && fye) {
        const iso = normalizeProfileDateToIso(fye);
        if (iso) suggestedFiscalYearEnd.value = iso;
      }
    }
    // Return Deadline 은 Circulation Date + 14 days 공식 기반 suggestion 으로 통일
    // (이전: agmDuedate/arDuedate 자동 채움 — 더 이상 사용 안 함)

    // Retiring Directors regulation 기본값 채움 (사용자 입력 없을 때만)
    applyElectionRegulationDefault();
  } catch (err) {
    console.warn('[PreSetAGM] entity profile load failed:', err);
  } finally {
    entityProfileLoading.value = false;
  }
}

const lastAutoElectionRegulation = ref<string>('');
function formatElectionRegulationBase(base: string): string {
  const b = String(base || '').trim();
  if (!b) return '';
  return /^(regulation|article|clause)\b/i.test(b) ? b : `Regulation ${b}`;
}

// 개별 director Regulation 선택용 옵션 (일괄 dropdown 과 동일 소스, 포맷 적용)
const regulationOptions = computed<string[]>(() =>
  Array.from(new Set(electionArticleOptions.value.map((o) => formatElectionRegulationBase(o)).filter(Boolean))),
);

function applyElectionRegulationDefault() {
  const formatted = formatElectionRegulationBase(electionArticleSelected.value || electionArticleNo.value);
  if (!formatted) return;
  lastAutoElectionRegulation.value = formatted;
  retiringDirectors.value = (retiringDirectors.value || []).map((rd) => ({
    ...rd,
    regulation: rd.regulation?.trim() ? rd.regulation : formatted,
  }));
}

watch(
  () => electionArticleSelected.value,
  (next, prev) => {
    // 빈값 선택(=세팅 안 함) 허용
    const nextFormatted = formatElectionRegulationBase(next);
    const prevFormatted = formatElectionRegulationBase(prev);
    if (!nextFormatted) {
      // 이전에 자동 채웠던 값과 동일한 regulation은 비워줌 (사용자 수동 입력은 보존)
      const autoVal = lastAutoElectionRegulation.value || prevFormatted;
      if (autoVal) {
        retiringDirectors.value = (retiringDirectors.value || []).map((rd) => ({
          ...rd,
          regulation: rd.regulation === autoVal ? '' : rd.regulation,
        }));
      }
      electionArticleNo.value = '';
      return;
    }
    electionArticleNo.value = next;
    lastAutoElectionRegulation.value = nextFormatted;
    retiringDirectors.value = (retiringDirectors.value || []).map((rd) => ({
      ...rd,
      regulation: rd.regulation?.trim() ? rd.regulation : nextFormatted,
    }));
  }
);
watch(
  () => icpCd.value,
  async (code) => {
    if (!code) return;
    entityInfoLoading.value = true;
    try {
      const res = await fetch(`/api/setupEntity/changeAddress/address-info/${code}`, {
        credentials: 'include',
      });
      const json = await res.json();
      if (json?.success) {
        regNo.value = json.regNo || '';
        if (!regNoOld.value) regNoOld.value = json.regNoOld || '';
        articleNo.value = json.articleNo || '';
        // Constitution Clause: Entity 의 ARTICLE_NO 텍스트로 결의 근거(basis) 를 판정.
        //   ① Third Schedule  → basis=third_schedule, clause N/A (전체 텍스트를 clause 번호로 넣지 않음!)
        //   ② Articles of Association → basis=articles, clause=번호, 라디오 잠금
        //   ③ 그 외(Constitution Clause) → basis=constitution, clause=번호, 라디오 잠금
        const regClause = String(json.clauseNo || '').trim();
        const isThirdSchedule = /third\s*schedule/i.test(regClause);
        const isArticles = /articles?\s+of\s+association/i.test(regClause);
        if (isThirdSchedule) {
          // Third Schedule = 정관 없음 → 등록 clause 아님(잠금 X). clause 필드는 N/A.
          entityClauseRegistered.value = false;
          resolutionBasis.value = 'third_schedule';
          constitutionClauseNo.value = '';
        } else if (regClause) {
          entityClauseRegistered.value = true;
          constitutionClauseNo.value = parseClauseNo(regClause); // 등록값 반영(잠금)
          resolutionBasis.value = isArticles ? 'articles' : 'constitution';
        } else {
          // 미등록 → 라디오 자유 선택(기본값 유지), select box 로 clause 입력
          entityClauseRegistered.value = false;
        }
      }
    } catch (err) {
      console.warn('[PreSetAGM] entity-info load failed:', err);
    } finally {
      entityInfoLoading.value = false;
    }

    // ── 임원/주주 자동 바인딩 (entity 변경 시 1회) ──
    await autoBindOfficers(code);
    // ── Entity profile 기반 자동 채움 (FYE/RegNo 등) ──
    await loadEntityProfile(code);
  },
  { immediate: true }
);

/**
 * Entity 의 Director / Shareholder 목록을 한 번에 가져와서
 * Retiring Directors / Members 폼에 자동 채움.
 * 사용자가 이미 수정한 데이터는 보호하지 않고 entity 단위로 덮어쓴다.
 */
async function autoBindOfficers(entityCd: string) {
  // Directors → Retiring Directors + suggestion 소스 (firstDirectorName)
  try {
    const dirRes: any = await docGenApi.getDirectors(entityCd);
    const dirs = dirRes?.data?.directors || dirRes?.data || [];
    if (Array.isArray(dirs) && dirs.length) {
      directorsList.value = dirs.map((d: any) => ({
        name: d.name || d.contactName || '',
      }));
      // EDIT 모드: 복원된 retiringDirectors(이름/regulation)를 entity 데이터로 덮어쓰지 않음
      //   (autoBind 가 restoreState 보다 늦게 resolve 되어 복원값을 지우는 경합 방지)
      if (!editMode.value) {
        retiringDirectors.value = dirs.map((d: any): RetiringDirectorInput => ({
          name: d.name || d.contactName || '',
          regulation: '',
        }));
      }
    }
  } catch (err) {
    console.warn('[PreSetAGM] getDirectors failed:', err);
  }

  // Secretaries → Smart default suggestion 소스
  try {
    const secRes: any = await docGenApi.getSecretaries(entityCd);
    const secs = secRes?.data?.secretaries || secRes?.data || [];
    if (Array.isArray(secs) && secs.length) {
      secretariesList.value = secs.map((s: any) => ({
        name: s.name || s.contactName || '',
      }));
    }
  } catch (err) {
    console.warn('[PreSetAGM] getSecretaries failed:', err);
  }

  // Members (= registered shareholders) — jurisdiction 별 분기는 docGenApi.getMembers 내부에서 처리
  try {
    const memRes: any = await docGenApi.getMembers(entityCd);
    const mems = memRes?.data?.members || memRes?.data?.shares || memRes?.data || [];
    // EDIT 모드: 복원된 members(이름/주소)를 entity 데이터로 덮어쓰지 않음
    //   (corporate 멤버는 primaryAddress 가 비어 와서 복원된 주소를 '' 로 지우는 경합 발생)
    if (Array.isArray(mems) && mems.length && !editMode.value) {
      members.value = mems.map((s: any): MemberInput => {
        const addrList = Array.isArray(s.addresses) ? s.addresses : [];
        return {
          name: s.name || s.contactName || `${s.firstName || ''} ${s.surname || s.lastName || ''}`.trim(),
          // 기본값: 단일 주소면 그 주소, 여러 건이면 첫 번째 chip 주소를 기본 셋팅 (chip 으로 교체 가능)
          address: s.primaryAddress || s.address || s.residentialAddress || (addrList[0]?.value || ''),
          addresses: addrList,
          contactCd: s.contactCd || '',
        };
      });
    }
  } catch (err) {
    console.warn('[PreSetAGM] getMembers failed:', err);
  }
}

// ─── 공통 입력 (Date fields follow session dateFormat) ───
const {
  dateValue: letterDate,
  dateDisplay: letterDateDisplay,
  datePickerValue: letterDatePickerValue,
  datePlaceholder: letterDatePlaceholder,
  dateMenu: letterDateMenu,
  datePickerProps: letterDatePickerProps,
} = useDateField();

const {
  dateValue: fiscalYearEndDate,
  dateDisplay: fiscalYearEndDateDisplay,
  datePickerValue: fiscalYearEndDatePickerValue,
  datePlaceholder: fiscalYearEndDatePlaceholder,
  dateMenu: fiscalYearEndDateMenu,
  datePickerProps: fiscalYearEndDatePickerProps,
} = useDateField();

const fiscalYearEndLabel = ref('');

const {
  dateValue: directorsResolutionDate,
  dateDisplay: directorsResolutionDateDisplay,
  datePickerValue: directorsResolutionDatePickerValue,
  datePlaceholder: directorsResolutionDatePlaceholder,
  dateMenu: directorsResolutionDateMenu,
  datePickerProps: directorsResolutionDatePickerProps,
} = useDateField();

const {
  dateValue: circulationDate,
  dateDisplay: circulationDateDisplay,
  datePickerValue: circulationDatePickerValue,
  datePlaceholder: circulationDatePlaceholder,
  dateMenu: circulationDateMenu,
  datePickerProps: circulationDatePickerProps,
} = useDateField();

const {
  dateValue: returnDeadline,
  dateDisplay: returnDeadlineDisplay,
  datePickerValue: returnDeadlinePickerValue,
  datePlaceholder: returnDeadlinePlaceholder,
  dateMenu: returnDeadlineMenu,
  datePickerProps: returnDeadlinePickerProps,
} = useDateField();

const {
  dateValue: certificationDate,
  dateDisplay: certificationDateDisplay,
  datePickerValue: certificationDatePickerValue,
  datePlaceholder: certificationDatePlaceholder,
  dateMenu: certificationDateMenu,
  datePickerProps: certificationDatePickerProps,
} = useDateField();

const statutoryDeclarantName = ref('');
const certifyingSecretaryName = ref('');
const resolutionText = ref('');

const directorCount = ref<number>(2);
const hasConstitution = ref(true);
const constitutionClauseNo = ref('');
// Entity 에 정관 조항(ARTICLE_NO)이 등록돼 있으면 true → 입력칸 readonly(변경 불가)
const entityClauseRegistered = ref(false);
// 미등록 시 선택용 옵션 (L_ARTICLE_NO 마스터, /resolution-options 메모리 캐시)
const articleNoOptions = ref<string[]>([]);
// 결의 근거 3분기 (DCR if 분기용). hasConstitution 은 이로부터 파생 동기화.
const resolutionBasis = ref<'constitution' | 'articles' | 'third_schedule'>('constitution');
watch(resolutionBasis, (b) => { hasConstitution.value = b !== 'third_schedule'; });

/** 등록된 clause 옵션 문자열이 어떤 Governing Document 종류인지 분류. */
function clauseOptionType(opt: string): 'constitution' | 'articles' | 'third_schedule' | 'other' {
  const s = String(opt || '');
  // 우선순위: Third Schedule > Articles(Article(s)·구 Table A·Regulation·1965·LLP) > Constitution(Clause).
  if (/third\s+schedule/i.test(s)) return 'third_schedule';
  if (/\barticles?\b/i.test(s) || /\bregulation\b/i.test(s) || /\btable\s+a\b/i.test(s) || /\bllp\b/i.test(s)) return 'articles';
  if (/\bconstitution\b/i.test(s) || /\bclause\b/i.test(s)) return 'constitution';
  return 'other';
}
/** 선택된 결의 근거(resolutionBasis)에 맞는 clause 옵션만 노출 (분류 불가 'other' 는 유지). */
const filteredClauseOptions = computed<string[]>(() =>
  articleNoOptions.value.filter((o) => {
    const ot = clauseOptionType(o);
    return ot === resolutionBasis.value || ot === 'other';
  })
);

/** "Article 98 of the Constitution" 같은 전체 텍스트에서 조항 번호만 추출 */
function parseClauseNo(input: string): string {
  const s = String(input || '').trim();
  if (!s) return '';
  const m1 = s.match(/(?:Article|Clause)\s+([\w()]+)/i);
  if (m1) return m1[1];
  if (!/article|clause|constitution|association/i.test(s)) return s;
  const m2 = s.match(/\b(\d+[\w()]*)/);
  return m2 ? m2[1] : s;
}

/** select/입력값을 parseClauseNo 로 정규화해서 저장 */
const constitutionClauseModel = computed<string>({
  get: () => constitutionClauseNo.value,
  set: (val: string | null) => { constitutionClauseNo.value = parseClauseNo(val || ''); },
});

// ─── Audited 전용 ───
//   Auditor Firm Name 은 자동 바인딩하지 않음. ✨ 아이콘 클릭 → 임원(Auditor) 선택 챗봇으로 입력.
const auditorFirmName = ref('');
const auditorAddress = ref('');

// ─── Lodger (Audit Exemption Certificate) ───
const lodgerName = ref('');
const lodgerNric = ref('');
const lodgerAddress = ref('');
const lodgerPhone = ref('');
const lodgerEmail = ref('');

// ─── Validation UI (required fields highlight) ───
const showValidationErrors = ref(false);
type RequiredKey =
  | 'letterDate'
  | 'fiscalYearEndDate'
  | 'directorsResolutionDate'
  | 'circulationDate'
  | 'returnDeadline'
  | 'certificationDate'
  | 'statutoryDeclarantName'
  | 'certifyingSecretaryName'
  | 'constitutionClauseNo'
  | 'auditorFirmName'
  | 'lodgerName'
  | 'lodgerNric'
  | 'lodgerAddress'
  | 'lodgerPhone'
  | 'lodgerEmail'
  | 'dividendType'
  | 'dividendPerShare'
  | 'dividendTotalAmount'
  | 'dividendRecordDate'
  | 'dividendPaymentDate'
  | 'directorsRemunerationTotal';

type ValidationItem = { key: RequiredKey; label: string; section: 'dates' | 'resolution' | 'conditions' | 'auditor' | 'lodger' | 'dividend' | 'remuneration' };

function isEmpty(v: any): boolean {
  if (v === null || v === undefined) return true;
  if (typeof v === 'string') return v.trim() === '';
  return false;
}

const missingRequired = computed<ValidationItem[]>(() => {
  const out: ValidationItem[] = [];
  // Dates
  if (isEmpty(letterDate.value)) out.push({ key: 'letterDate', label: 'Letter Date', section: 'dates' });
  if (isEmpty(fiscalYearEndDate.value)) out.push({ key: 'fiscalYearEndDate', label: 'Fiscal Year End', section: 'dates' });
  if (isEmpty(directorsResolutionDate.value)) out.push({ key: 'directorsResolutionDate', label: "Directors' Resolution Date", section: 'dates' });
  if (isEmpty(circulationDate.value)) out.push({ key: 'circulationDate', label: 'Circulation Date', section: 'dates' });
  if (isEmpty(returnDeadline.value)) out.push({ key: 'returnDeadline', label: 'Return Deadline', section: 'dates' });
  if (isEmpty(certificationDate.value)) out.push({ key: 'certificationDate', label: 'Certification Date', section: 'dates' });

  // Resolution
  if (isEmpty(statutoryDeclarantName.value)) out.push({ key: 'statutoryDeclarantName', label: 'Statutory Declarant Name', section: 'resolution' });
  if (isEmpty(certifyingSecretaryName.value)) out.push({ key: 'certifyingSecretaryName', label: 'Certifying Secretary Name', section: 'resolution' });

  // Conditions
  if (hasConstitution.value && isEmpty(constitutionClauseNo.value)) out.push({ key: 'constitutionClauseNo', label: 'Constitution Clause No', section: 'conditions' });

  // Auditor (audited only)
  if (docFlags.value.isAudited && isEmpty(auditorFirmName.value)) out.push({ key: 'auditorFirmName', label: 'Auditor Firm Name', section: 'auditor' });

  // Lodger (audit exemption)
  if (docFlags.value.hasAuditExemption) {
    if (isEmpty(lodgerName.value)) out.push({ key: 'lodgerName', label: 'Lodger Name', section: 'lodger' });
    if (isEmpty(lodgerNric.value)) out.push({ key: 'lodgerNric', label: 'Lodger NRIC', section: 'lodger' });
    if (isEmpty(lodgerAddress.value)) out.push({ key: 'lodgerAddress', label: 'Lodger Address', section: 'lodger' });
    if (isEmpty(lodgerPhone.value)) out.push({ key: 'lodgerPhone', label: 'Lodger Phone', section: 'lodger' });
    if (isEmpty(lodgerEmail.value)) out.push({ key: 'lodgerEmail', label: 'Lodger Email', section: 'lodger' });
  }

  // Dividend — 사용자가 Has Dividend = Yes 체크한 경우에만 필수 검증
  if (docFlags.value.hasDividend && hasDividend.value) {
    if (isEmpty(dividendType.value)) out.push({ key: 'dividendType', label: 'Dividend Type', section: 'dividend' });
    if (isEmpty(dividendPerShare.value)) out.push({ key: 'dividendPerShare', label: 'Per Share (RM)', section: 'dividend' });
    if (isEmpty(dividendTotalAmount.value)) out.push({ key: 'dividendTotalAmount', label: 'Total Amount (RM)', section: 'dividend' });
    if (isEmpty(dividendRecordDate.value)) out.push({ key: 'dividendRecordDate', label: 'Record Date', section: 'dividend' });
    if (isEmpty(dividendPaymentDate.value)) out.push({ key: 'dividendPaymentDate', label: 'Payment Date', section: 'dividend' });
  }

  // Remuneration
  if (docFlags.value.hasRemuneration && isEmpty(directorsRemunerationTotal.value)) {
    out.push({ key: 'directorsRemunerationTotal', label: 'Total Remuneration (RM)', section: 'remuneration' });
  }

  return out;
});

function hasSectionErrors(section: ValidationItem['section']): boolean {
  if (!showValidationErrors.value) return false;
  return missingRequired.value.some(x => x.section === section);
}

function hasFieldError(key: RequiredKey): boolean {
  if (!showValidationErrors.value) return false;
  return missingRequired.value.some(x => x.key === key);
}

// ─── Dividend ───
/**
 * 사용자가 명시적으로 배당 여부를 결정하는 핵심 플래그.
 * - false: Dividend 입력폼 숨김 + DCR & MWR (Declaration of Dividend) 문서 생성 제외
 * - true:  Dividend 입력폼 표시 + 해당 문서 생성
 * Default false (opt-in) — 불필요한 배당 문서 생성 방지.
 */
const hasDividend = ref(false);
const dividendType = ref<'interim' | 'final' | ''>('');
const dividendPerShare = ref('');
const dividendTotalAmount = ref('');
const dividendRecordDate = ref('');
const dividendPaymentDate = ref('');

// ─── Remuneration ───
const directorsRemunerationTotal = ref('');

// ─── Smart default 데이터 (Statutory Declarant / Certifying Secretary 제안용) ───
const directorsList = ref<Array<{ name: string }>>([]);
const secretariesList = ref<Array<{ name: string }>>([]);

const suggestedDeclarantName = computed(() => directorsList.value[0]?.name || '');
const suggestedSecretaryName = computed(() => secretariesList.value[0]?.name || '');

// ─── Retiring Directors (loop) ───
const retiringDirectors = ref<RetiringDirectorInput[]>([]);
function addRetiringDirector() {
  retiringDirectors.value.push({ name: '', regulation: '' });
}
function removeRetiringDirector(idx: number) {
  retiringDirectors.value.splice(idx, 1);
}

// ─── Members (loop) ───
const members = ref<MemberInput[]>([]);
function addMember() {
  members.value.push({ name: '', address: '' });
}
function removeMember(idx: number) {
  members.value.splice(idx, 1);
}

const dividendTypeOptions = [
  { value: 'interim', text: 'Interim' },
  { value: 'final', text: 'Final' },
];

/**
 * FYE(YYYY-MM-DD) → "YEAR ENDED D MONTH YYYY"
 * (agmMapper의 deriveFiscalYearEndLabel 로직과 동일한 형태)
 */
function deriveFyeLabelFromIso(iso: string): string {
  if (!iso) return '';
  const m = iso.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (!m) return '';
  const [, y, mm, dd] = m;
  const months = [
    'JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE',
    'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER',
  ];
  const mi = Math.max(0, Math.min(11, Number(mm) - 1));
  return `YEAR ENDED ${Number(dd)} ${months[mi]} ${y}`;
}

// ─── Date suggestions (자동 채우지 않고 사용자가 수락하도록 제안만) ───
function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function addDaysIso(iso: string, days: number): string {
  if (!iso) return '';
  const m = iso.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (!m) return '';
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (isNaN(d.getTime())) return '';
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatIsoForDisplay(iso: string): string {
  if (!iso) return '';
  const m = iso.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (!m) return iso;
  return `${m[3].padStart(2, '0')}/${m[2].padStart(2, '0')}/${m[1]}`;
}

/** Fiscal Year End — DB(entity profile) 에서 가져온 제안값. 자동 채움 X */
const suggestedFiscalYearEnd = ref('');

const suggestedLetterDate = computed(() => todayIso());
const suggestedCirculationDate = computed(() => todayIso());
const suggestedDirectorsResolutionDate = computed(() => {
  const base = letterDate.value || suggestedLetterDate.value;
  return base ? addDaysIso(base, -3) : '';
});
const suggestedReturnDeadline = computed(() => {
  const base = circulationDate.value || suggestedCirculationDate.value;
  return base ? addDaysIso(base, 14) : '';
});
const suggestedCertificationDate = computed(() => {
  return circulationDate.value || suggestedCirculationDate.value;
});
const suggestedFiscalYearEndLabel = computed(() => {
  const fye = fiscalYearEndDate.value || suggestedFiscalYearEnd.value;
  return fye ? deriveFyeLabelFromIso(fye) : '';
});

/**
 * 사용자가 X 클릭으로 dismiss 한 suggestion 추적.
 * key = `${field}:${value}` — suggestion 값이 바뀌면 다시 노출됨 (예: letter 변경 → resolution 재계산 → 다시 표시).
 */
const dismissedSuggestions = ref<Set<string>>(new Set());
function suggestionKey(field: string, value: string): string {
  return `${field}:${value}`;
}
function isSuggestionDismissed(field: string, value: string): boolean {
  if (!value) return true;
  return dismissedSuggestions.value.has(suggestionKey(field, value));
}
function dismissSuggestion(field: string, value: string) {
  if (!value) return;
  const next = new Set(dismissedSuggestions.value);
  next.add(suggestionKey(field, value));
  dismissedSuggestions.value = next;
}
function addressSuggestionLabel(addr: any, idx: number, allAddrs: any[] | undefined): string {
  if (!allAddrs) return addr?.label || addr?.type || 'Address';
  if (addr?.type === 'branch') {
    const branchNo = allAddrs.slice(0, idx + 1).filter((a) => a?.type === 'branch').length;
    return `Branch ${branchNo}`;
  }
  if (addr?.label) return String(addr.label);
  const type = String(addr?.type || '').trim();
  return type ? type.charAt(0).toUpperCase() + type.slice(1) : 'Address';
}

// ─── Person Lookup ───
function requestLookup(ctx: PersonLookupContext) {
  emit('request-person-lookup', ctx);
}

/** 멤버 주소가 비어 있고 chip 옵션이 있으면 첫 번째 chip 주소를 기본 셋팅 (load/restore 시점 1회) */
function normalizeMemberAddresses() {
  for (const m of members.value || []) {
    if (!String(m.address || '').trim() && Array.isArray(m.addresses) && m.addresses.length) {
      const first = m.addresses[0]?.value;
      if (first) m.address = first;
    }
  }
}

// ─── Address Chooser (member 의 addresses.length > 1 인 경우) ───
function requestAddressChoice(memberIdx: number) {
  const m = members.value[memberIdx];
  if (!m || !Array.isArray(m.addresses) || m.addresses.length <= 1) return;
  emit('request-address-lookup', {
    fieldGroup: 'member',
    index: memberIdx,
    addresses: m.addresses,
    subjectLabel: m.name || `Member ${memberIdx + 1}`,
  });
}

/** 챗봇 chooser 에서 주소 선택 완료 → activeAddressContext.index 의 member 에 채움 */
watch(
  () => props.selectedAddress,
  (sel) => {
    if (!sel?.value) return;
    const ctx = props.activeAddressContext;
    if (!ctx || ctx.fieldGroup !== 'member') return;
    const target = members.value[ctx.index];
    if (!target) return;
    target.address = sel.value;
  }
);

/**
 * 챗봇에서 사람 선택 완료 시(selectedPerson 변경) 활성 컨텍스트에 따라 자동 채움
 */
/** 사람의 첫 번째 사용 가능 주소 (Residential 우선 폴백) — 1건이면 자동, 여러건이면 첫 주소 기본 */
function firstPersonAddress(p: any): string {
  if (!p) return '';
  const direct = p.primaryAddress || p.residentialAddress || p.address
    || p.businessAddress || p.serviceAddress || p.correspondenceAddress;
  if (direct && String(direct).trim()) return String(direct).trim();
  const arr = Array.isArray(p.addresses) ? p.addresses : [];
  for (const a of arr) {
    const v = typeof a === 'string'
      ? a
      : (a?.value || a?.address || a?.fullAddress || a?.formatted || a?.formattedAddress || '');
    if (v && String(v).trim()) return String(v).trim();
  }
  return '';
}

watch(
  () => props.selectedPerson,
  (person) => {
    if (!person) return;
    const ctx = props.activePersonContext;
    if (!ctx) return;
    const fullName = person.name || person.contactName || '';
    const nric = person.contactRegNo || '';
    const address = firstPersonAddress(person);
    const email = person.contactEmail || '';
    const phone = `${person.areaCode || ''}${person.mobileNo || ''}`.trim();

    switch (ctx.fieldGroup) {
      case 'retiringDirector': {
        const i = ctx.index ?? 0;
        if (retiringDirectors.value[i]) retiringDirectors.value[i].name = fullName;
        break;
      }
      case 'member': {
        const i = ctx.index ?? 0;
        if (members.value[i]) {
          members.value[i].name = fullName;
          if (address) members.value[i].address = address;
        }
        break;
      }
      case 'lodger':
        lodgerName.value = fullName;
        if (nric) lodgerNric.value = nric;
        if (address) lodgerAddress.value = address;
        if (phone) lodgerPhone.value = phone;
        if (email) lodgerEmail.value = email;
        break;
      case 'statutoryDeclarant':
        statutoryDeclarantName.value = fullName;
        break;
      case 'certifyingSecretary':
        certifyingSecretaryName.value = fullName;
        break;
      case 'auditor':
        // Auditor 는 회사명(firm name) 우선 — Corporate contact 면 contactName 자체가 firm name
        auditorFirmName.value = fullName;
        if (address) auditorAddress.value = address;
        break;
      case 'auditorAddress':
        // 주소만 가져오기 — Auditor Firm Name 은 session.customer.name 기본값 보존.
        if (address) auditorAddress.value = address;
        break;
    }
  },
);

// ─── 필수 입력 검증 (간단) ───
const isFormValid = computed(() => {
  if (!letterDate.value || !fiscalYearEndDate.value || !directorsResolutionDate.value) return false;
  if (!circulationDate.value || !returnDeadline.value || !certificationDate.value) return false;
  if (!statutoryDeclarantName.value || !certifyingSecretaryName.value) return false;
  if (hasConstitution.value && !constitutionClauseNo.value.trim()) return false;
  if (docFlags.value.hasAuditExemption) {
    if (!lodgerName.value || !lodgerNric.value || !lodgerAddress.value || !lodgerPhone.value || !lodgerEmail.value) return false;
  }
  if (docFlags.value.hasDividend && hasDividend.value) {
    if (!dividendType.value || !dividendPerShare.value || !dividendTotalAmount.value) return false;
    if (!dividendRecordDate.value || !dividendPaymentDate.value) return false;
  }
  if (docFlags.value.hasRemuneration && !directorsRemunerationTotal.value) return false;
  if (docFlags.value.isAudited && !auditorFirmName.value) return false;
  return true;
});

// ─── Drag & Drop (챗봇 카드 → 폼 필드) ───
// VariableFormFields.vue 와 동일 패턴: 글로벌 dragstart/dragend 로 isDragging 추적,
// 각 입력 필드 위에 transparent overlay 를 겹쳐서 Vuetify 의 이벤트 가로채기를 우회.
const isDragging = ref(false);
function _onGlobalDragStart() { isDragging.value = true; }
function _onGlobalDragEnd() { isDragging.value = false; }

/** dataTransfer 에서 값 추출 후 callback 호출 */
function handleFieldDrop(event: DragEvent, setter: (val: string) => void) {
  isDragging.value = false;
  if (!event.dataTransfer) return;
  const json = event.dataTransfer.getData('application/x-entity-field');
  if (json) {
    try {
      const { value } = JSON.parse(json);
      if (value) {
        setter(String(value));
        return;
      }
    } catch { /* ignore */ }
  }
  const text = event.dataTransfer.getData('text/plain');
  if (text) setter(String(text));
}

// ─── Past AGM tasks 로드 (이전 작업 불러오기) ───
const pastTasks = ref<Array<{
  taskId: number;
  taskTitle: string;
  taskTypeCd: string;
  taskTypeName: string;
  sysregdate: string;
  sysregname: string;
  taskStatus: string;
  docCount: number;
  formData: Record<string, any> | null;
}>>([]);
const pastTasksLoaded = ref(false);
const loadPrevDialog = ref(false);

async function fetchPastAgmTasks() {
  if (!icpCd.value) {
    pastTasks.value = [];
    pastTasksLoaded.value = true;
    return;
  }
  try {
    const res = await fetch(`/api/startTask/tasks/past-agm/${icpCd.value}`, {
      credentials: 'include',
    });
    const json = await res.json();
    pastTasks.value = (json?.data?.tasks || []).filter((t: any) => t.formData);
  } catch (err) {
    console.warn('[PreSetAGM] fetchPastAgmTasks failed:', err);
    pastTasks.value = [];
  } finally {
    pastTasksLoaded.value = true;
  }
}

watch(
  () => icpCd.value,
  (cd) => {
    if (cd) {
      pastTasksLoaded.value = false;
      fetchPastAgmTasks();
    } else {
      pastTasks.value = [];
      pastTasksLoaded.value = false;
    }
  },
  { immediate: true }
);

function formatPastDate(s: string): string {
  if (!s) return '';
  // SYSREGDATE 는 'YYYY-MM-DD HH:mm:ss' 형식
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return s;
  return `${m[3]}/${m[2]}/${m[1]}`;
}

/** Past task 의 DATA_JSON 을 form refs 로 unmap */
function applyPastTask(task: { taskId: number; taskTitle: string; formData: Record<string, any> | null }) {
  const fd = task.formData;
  if (!fd) return;
  const get = (k: string) => (fd[k] != null ? String(fd[k]) : '');
  const getBool = (k: string) => fd[k] === true || fd[k] === 'true' || fd[k] === 1 || fd[k] === '1';
  const getNum = (k: string) => {
    const v = fd[k];
    if (v == null || v === '') return 0;
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  // Dates
  if (get('letter_date')) letterDate.value = get('letter_date');
  if (get('fiscal_year_end_date')) fiscalYearEndDate.value = get('fiscal_year_end_date');
  if (get('fiscal_year_end_label')) fiscalYearEndLabel.value = get('fiscal_year_end_label');
  if (get('directors_resolution_date')) directorsResolutionDate.value = get('directors_resolution_date');
  if (get('circulation_date')) circulationDate.value = get('circulation_date');
  if (get('return_deadline')) returnDeadline.value = get('return_deadline');
  if (get('certification_date')) certificationDate.value = get('certification_date');

  // Resolution & Signatories
  if (get('statutory_declarant_name')) statutoryDeclarantName.value = get('statutory_declarant_name');
  if (get('certifying_secretary_name')) certifyingSecretaryName.value = get('certifying_secretary_name');
  if (get('resolution_text')) resolutionText.value = get('resolution_text');

  // Conditions
  if (fd.director_count != null) directorCount.value = getNum('director_count') || 2;
  if (get('constitution_clause_no')) constitutionClauseNo.value = get('constitution_clause_no');
  // 결의 근거: 신규 3분기 플래그 우선, 없으면 has_constitution(2분기)으로 역호환
  if (getBool('resolution_basis_articles')) resolutionBasis.value = 'articles';
  else if (getBool('resolution_basis_third_schedule')) resolutionBasis.value = 'third_schedule';
  else if (getBool('resolution_basis_constitution')) resolutionBasis.value = 'constitution';
  else if (fd.has_constitution != null) resolutionBasis.value = getBool('has_constitution') ? 'constitution' : 'third_schedule';

  // Has Dividend toggle
  if (fd.dividend_type || fd.dividend_per_share || fd.dividend_total_amount) {
    hasDividend.value = true;
  }

  // Auditor
  if (get('auditor_firm_name')) auditorFirmName.value = get('auditor_firm_name');
  if (get('auditor_address')) auditorAddress.value = get('auditor_address');

  // Lodger
  if (get('lodger_name')) lodgerName.value = get('lodger_name');
  if (get('lodger_nric')) lodgerNric.value = get('lodger_nric');
  if (get('lodger_address')) lodgerAddress.value = get('lodger_address');
  if (get('lodger_phone')) lodgerPhone.value = get('lodger_phone');
  if (get('lodger_email')) lodgerEmail.value = get('lodger_email');

  // Dividend
  if (get('dividend_type')) dividendType.value = get('dividend_type') as any;
  if (get('dividend_per_share')) dividendPerShare.value = get('dividend_per_share');
  if (get('dividend_total_amount')) dividendTotalAmount.value = get('dividend_total_amount');
  if (get('dividend_record_date')) dividendRecordDate.value = get('dividend_record_date');
  if (get('dividend_payment_date')) dividendPaymentDate.value = get('dividend_payment_date');

  // Remuneration
  if (get('directors_remuneration_total')) directorsRemunerationTotal.value = get('directors_remuneration_total');

  // Loops — retiringDirectors, members
  if (Array.isArray(fd.retiring_directors)) {
    retiringDirectors.value = fd.retiring_directors.map((r: any) => ({
      name: r.retiring_director_name || r.name || '',
      regulation: r.retiring_director_regulation || r.regulation || '',
    }));
  }
  if (Array.isArray(fd.members)) {
    members.value = fd.members.map((m: any) => ({
      name: m.member_name || m.name || '',
      address: m.member_address || m.address || '',
      addresses: [],
      contactCd: '',
    }));
  }

  loadPrevDialog.value = false;
  progress.value = `Loaded data from "${task.taskTitle || `Task #${task.taskId}`}".`;
}

onMounted(async () => {
  // 글로벌 드래그 상태 추적 (필드 위 overlay 노출용)
  document.addEventListener('dragstart', _onGlobalDragStart);
  document.addEventListener('dragend', _onGlobalDragEnd);

  // Constitution Clause 선택 옵션 (L_ARTICLE_NO 마스터, 메모리 캐시라 즉시 응답)
  try {
    const res = await fetch('/api/entity_my/profile/resolution-options', { credentials: 'include' });
    if (res.ok) {
      const json = await res.json();
      const opts = json?.articleNoOptions || json?.data?.articleNoOptions || [];
      articleNoOptions.value = Array.isArray(opts)
        ? Array.from(new Set(opts.map((o: any) => String(o?.value ?? o?.ArticleNo ?? o?.articleNo ?? o ?? '').trim()).filter(Boolean)))
        : [];
    }
  } catch (err) {
    console.warn('[PreSetAGM] resolution-options load failed:', err);
  }

  try {
    const taskCode = props.selection.task.TASK_CODE;
    if (taskCode) {
      const cfg = await getTaskConfigByCode(taskCode);
      taskConfigSteps.value = cfg?.processSteps || [];
    }
  } catch (err) {
    console.warn('[PreSetAGM] Task config fetch failed:', err);
  }
});

onUnmounted(() => {
  document.removeEventListener('dragstart', _onGlobalDragStart);
  document.removeEventListener('dragend', _onGlobalDragEnd);
});

async function handleCreateTask() {
  const title = effectiveTaskTitle.value;
  if (!title) {
    progress.value = 'Error: Task Title is required — please enter it in the chat.';
    return;
  }
  if (!props.entityData) {
    progress.value = 'Error: Please select an entity in the chat first.';
    return;
  }
  if (!isFormValid.value) {
    showValidationErrors.value = true;
    progress.value = 'Error: Please fill all required AGM fields.';
    return;
  }

  const taskTypeCd = props.selection.task.TASK_CODE;
  if (!taskTypeCd) {
    progress.value = 'Error: Task Code not configured in PreSet.';
    return;
  }

  creating.value = true;
  progress.value = 'Creating task...';

  try {
    const steps = (taskConfigSteps.value || []).map(s => String(s).toLowerCase());
    const taskFlow = {
      createDocument: steps.length === 0 ? true : steps.includes('doc'),
      requestTSign: steps.includes('t-sign'),
      proceedWithEFiling: steps.includes('e-filing'),
    };

    // officerData → TASK_DATA.formData 로 저장. presetForm(Edit 복원 스냅샷) 포함.
    const officerData = { presetForm: gatherState() };

    let taskId: number | null = null;
    if (editMode.value) {
      // ── Update 분기 ──
      taskId = Number(props.editTaskId) || null;
      if (!taskId) { progress.value = 'Error: invalid edit task id.'; creating.value = false; return; }
      progress.value = 'Updating task...';
      try {
        await fetch(`/api/startTask/tasks/${taskId}/data`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ taskData: { formData: officerData } }),
        });
      } catch (err) {
        progress.value = `Error: ${err instanceof Error ? err.message : 'Task update failed'}`;
        creating.value = false; return;
      }
      // 제목 갱신 (선택)
      try {
        await fetch(`/api/startTask/tasks/${taskId}/title`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ taskTitle: props.taskTitle }),
        });
      } catch { /* title optional */ }
      progress.value = `Updating documents (Task ${taskId})...`;
    } else {
      // ── Create 분기 ──
      const taskRes = await fetch('/api/startTask/tasks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          entityCd: icpCd.value || null,
          taskTypeCd,
          taskTypeName: props.selection.task.TASK_NAME,
          taskTitle: title,
          taskFlow,
          officerData,
        }),
      });
      const taskJson = await taskRes.json();
      taskId = taskJson?.data?.taskId || taskJson?.taskId || null;
      if (!taskId) {
        progress.value = `Error: ${taskJson?.error?.message || taskJson?.message || 'Task creation failed'}`;
        creating.value = false;
        return;
      }
      progress.value = `Task created (ID: ${taskId}). Saving documents...`;
    }

    const commonFormData = buildAGMVariables({
      companyName: entityName.value,
      registrationNo: regNo.value,
      registrationNoOld: regNoOld.value,
      letterDate: letterDate.value,
      fiscalYearEndDate: fiscalYearEndDate.value,
      fiscalYearEndLabel: fiscalYearEndLabel.value,
      directorsResolutionDate: directorsResolutionDate.value,
      circulationDate: circulationDate.value,
      returnDeadline: returnDeadline.value,
      certificationDate: certificationDate.value,
      statutoryDeclarantName: statutoryDeclarantName.value,
      certifyingSecretaryName: certifyingSecretaryName.value,
      resolutionText: resolutionText.value,
      directorCount: directorCount.value,
      hasConstitution: hasConstitution.value,
      constitutionClauseNo: constitutionClauseNo.value,
      resolutionBasis: resolutionBasis.value,
      retiringDirectors: retiringDirectors.value,
      members: members.value,
      auditorFirmName: auditorFirmName.value,
      auditorAddress: auditorAddress.value,
      lodgerName: lodgerName.value,
      lodgerNric: lodgerNric.value,
      lodgerAddress: lodgerAddress.value,
      lodgerPhone: lodgerPhone.value,
      lodgerEmail: lodgerEmail.value,
      dividendType: dividendType.value,
      dividendPerShare: dividendPerShare.value,
      dividendTotalAmount: dividendTotalAmount.value,
      dividendRecordDate: dividendRecordDate.value,
      dividendPaymentDate: dividendPaymentDate.value,
      directorsRemunerationTotal: directorsRemunerationTotal.value,
    });

    // Bulk signer mapping (applies to all documents)
    if (signerAssignMode.value === 'bulk' && bulkSigners.value.length > 0) {
      (commonFormData as any).signers = bulkSigners.value;
    }

    // 상세페이지 'Bulk Set Signers' 다이얼로그용 사이너 풀 (incorporationPost/allotment 와 동일 형식).
    //   directors = entity 전체 이사 / cosecs = 비서 / allottees(=members) = 멤버
    (commonFormData as any).__directorsRaw = (directorsList.value || [])
      .map((d) => ({ name: String(d?.name || '').trim(), email: '' }))
      .filter((d) => d.name);
    (commonFormData as any).__cosecsRaw = (secretariesList.value || [])
      .map((s) => ({ name: String(s?.name || '').trim(), email: '' }))
      .filter((s) => s.name);
    (commonFormData as any).__allotteesRaw = (members.value || [])
      .map((m) => ({ name: String(m?.name || '').trim() }))
      .filter((m) => m.name);

    // Has Dividend=No 면 "Declaration of Dividend" 류 문서는 생성 대상에서 제외
    const isDividendDoc = (name: string) =>
      /declaration\s*of\s*dividend|dividend/i.test(name || '');

    const docsToSave = (props.selection.documents || [])
      .filter(d => d.tmpId)
      .filter(d => {
        if (!hasDividend.value && isDividendDoc(d.templateName || '')) {
          console.info('[PreSetAGM] Skipping dividend doc (Has Dividend = No):', d.templateName);
          return false;
        }
        return true;
      })
      .map(d => ({
        templateId: d.tmpId,
        label: d.templateName,
        personName: entityName.value || '',
        formData: { ...commonFormData },
        entityCd: icpCd.value || undefined,
      }));

    if (docsToSave.length > 0) {
      try {
        const saveRes = await fetch('/api/aiDocuments/incorporation/prepare-documents', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ taskId, documents: docsToSave, replaceExisting: editMode.value }),
        });
        const saveJson = await saveRes.json();
        if (!saveJson?.success) {
          progress.value = `Documents save warning: ${saveJson?.message || 'unknown'}`;
        }
      } catch (err: any) {
        console.warn('[PreSetAGM] Document save failed:', err);
      }
    }

    try {
      await fetch(`/api/startTask/tasks/${taskId}/document-status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
    } catch { /* ignore */ }

    try {
      await fetch('/api/startTask/preset/use-log', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ presetTaskId: props.selection.task.PRESET_TASK_ID, taskId }),
      });
    } catch { /* ignore */ }

    // Prefetch next page payload to avoid initial loading spinner
    try {
      progress.value = 'Preparing next screen...';
      const [taskDetailRes, docListRes] = await Promise.all([
        fetch(`/api/startTask/tasks/${taskId}`, { credentials: 'include' }).then((r) => r.json()).catch(() => null),
        fetch(`/api/startTask/doc-cust-save/${taskId}`, { credentials: 'include' }).then((r) => r.json()).catch(() => null),
      ]);
      const cacheKey = `presetTaskDetailPrefetch:${taskId}`;
      sessionStorage.setItem(cacheKey, JSON.stringify({ taskDetail: taskDetailRes, docList: docListRes }));
    } catch { /* ignore */ }

    emit('task-created', { taskId, taskTitle: title });
    router.push({ name: 'PreSetTaskDetail', query: { taskId: String(taskId) } });
  } catch (err: any) {
    console.error('[PreSetAGM] create task failed:', err);
    progress.value = `Error: ${err.message}`;
  } finally {
    creating.value = false;
  }
}

// ─── Edit 지원: 전체 입력 스냅샷 gather/restore (호스트가 TASK_DATA.formData.presetForm 로 저장/복원) ───
function gatherState(): Record<string, any> {
  return {
    regNo: regNo.value,
    regNoOld: regNoOld.value,
    articleNo: articleNo.value,
    electionArticleNo: electionArticleNo.value,
    electionArticleSelected: electionArticleSelected.value,
    letterDate: letterDate.value,
    fiscalYearEndDate: fiscalYearEndDate.value,
    fiscalYearEndLabel: fiscalYearEndLabel.value,
    directorsResolutionDate: directorsResolutionDate.value,
    circulationDate: circulationDate.value,
    returnDeadline: returnDeadline.value,
    certificationDate: certificationDate.value,
    statutoryDeclarantName: statutoryDeclarantName.value,
    certifyingSecretaryName: certifyingSecretaryName.value,
    resolutionText: resolutionText.value,
    directorCount: directorCount.value,
    hasConstitution: hasConstitution.value,
    constitutionClauseNo: constitutionClauseNo.value,
    resolutionBasis: resolutionBasis.value,
    auditorFirmName: auditorFirmName.value,
    auditorAddress: auditorAddress.value,
    lodgerName: lodgerName.value,
    lodgerNric: lodgerNric.value,
    lodgerAddress: lodgerAddress.value,
    lodgerPhone: lodgerPhone.value,
    lodgerEmail: lodgerEmail.value,
    hasDividend: hasDividend.value,
    dividendType: dividendType.value,
    dividendPerShare: dividendPerShare.value,
    dividendTotalAmount: dividendTotalAmount.value,
    dividendRecordDate: dividendRecordDate.value,
    dividendPaymentDate: dividendPaymentDate.value,
    directorsRemunerationTotal: directorsRemunerationTotal.value,
    retiringDirectors: JSON.parse(JSON.stringify(retiringDirectors.value || [])),
    members: JSON.parse(JSON.stringify(members.value || [])),
    bulkSigners: JSON.parse(JSON.stringify(bulkSigners.value || [])),
    signerAssignMode: signerAssignMode.value,
    dismissedSuggestions: Array.from(dismissedSuggestions.value),
  };
}

function restoreState(s: Record<string, any> | null | undefined) {
  if (!s || typeof s !== 'object') return;
  if (s.regNo != null) regNo.value = s.regNo;
  if (s.regNoOld != null) regNoOld.value = s.regNoOld;
  if (s.articleNo != null) articleNo.value = s.articleNo;
  if (s.electionArticleNo != null) electionArticleNo.value = s.electionArticleNo;
  if (s.electionArticleSelected != null) electionArticleSelected.value = s.electionArticleSelected;
  if (s.letterDate != null) letterDate.value = s.letterDate;
  if (s.fiscalYearEndDate != null) fiscalYearEndDate.value = s.fiscalYearEndDate;
  if (s.fiscalYearEndLabel != null) fiscalYearEndLabel.value = s.fiscalYearEndLabel;
  if (s.directorsResolutionDate != null) directorsResolutionDate.value = s.directorsResolutionDate;
  if (s.circulationDate != null) circulationDate.value = s.circulationDate;
  if (s.returnDeadline != null) returnDeadline.value = s.returnDeadline;
  if (s.certificationDate != null) certificationDate.value = s.certificationDate;
  if (s.statutoryDeclarantName != null) statutoryDeclarantName.value = s.statutoryDeclarantName;
  if (s.certifyingSecretaryName != null) certifyingSecretaryName.value = s.certifyingSecretaryName;
  if (s.resolutionText != null) resolutionText.value = s.resolutionText;
  if (s.directorCount != null) directorCount.value = Number(s.directorCount) || 2;
  if (s.constitutionClauseNo != null) constitutionClauseNo.value = s.constitutionClauseNo;
  // 결의 근거: 신규 resolutionBasis 우선, 없으면 hasConstitution(구버전)으로 역호환
  if (s.resolutionBasis === 'constitution' || s.resolutionBasis === 'articles' || s.resolutionBasis === 'third_schedule') {
    resolutionBasis.value = s.resolutionBasis;
  } else if (typeof s.hasConstitution === 'boolean') {
    resolutionBasis.value = s.hasConstitution ? 'constitution' : 'third_schedule';
  }
  if (s.auditorFirmName != null) auditorFirmName.value = s.auditorFirmName;
  if (s.auditorAddress != null) auditorAddress.value = s.auditorAddress;
  if (s.lodgerName != null) lodgerName.value = s.lodgerName;
  if (s.lodgerNric != null) lodgerNric.value = s.lodgerNric;
  if (s.lodgerAddress != null) lodgerAddress.value = s.lodgerAddress;
  if (s.lodgerPhone != null) lodgerPhone.value = s.lodgerPhone;
  if (s.lodgerEmail != null) lodgerEmail.value = s.lodgerEmail;
  if (typeof s.hasDividend === 'boolean') hasDividend.value = s.hasDividend;
  if (s.dividendType != null) dividendType.value = s.dividendType;
  if (s.dividendPerShare != null) dividendPerShare.value = s.dividendPerShare;
  if (s.dividendTotalAmount != null) dividendTotalAmount.value = s.dividendTotalAmount;
  if (s.dividendRecordDate != null) dividendRecordDate.value = s.dividendRecordDate;
  if (s.dividendPaymentDate != null) dividendPaymentDate.value = s.dividendPaymentDate;
  if (s.directorsRemunerationTotal != null) directorsRemunerationTotal.value = s.directorsRemunerationTotal;
  if (Array.isArray(s.retiringDirectors)) retiringDirectors.value = s.retiringDirectors;
  if (Array.isArray(s.members)) members.value = s.members;
  if (Array.isArray(s.bulkSigners)) bulkSigners.value = s.bulkSigners;
  if (s.signerAssignMode != null) signerAssignMode.value = s.signerAssignMode;
  if (Array.isArray(s.dismissedSuggestions)) dismissedSuggestions.value = new Set(s.dismissedSuggestions);
  // 복원된 멤버 중 주소가 비어 있고 chip 옵션이 있으면 첫 번째 chip 주소를 기본 셋팅
  normalizeMemberAddresses();
  // 파생 watcher 가 정착한 뒤 기준선 캡처 → 이후 실제 변경만 dirty 로 감지.
  nextTick(() => { editBaseline.value = JSON.stringify(gatherState()); });
}

defineExpose({ gatherState, restoreState });
</script>

<template>
  <div class="agm-panel">
    <!-- 헤더 -->
    <div class="agm-header">
      <div>
        <div class="agm-title">{{ selection.task.TASK_NAME }}</div>
        <div class="agm-subtitle">Annual General Meeting</div>
      </div>
      <v-btn variant="text" size="small" @click="emit('back')">
        <v-icon start size="16">mdi-arrow-left</v-icon>
        Back
      </v-btn>
    </div>

    <div class="agm-content">
      <!-- Task Title -->
      <div class="agm-section">
        <div class="agm-section-header">
          <Icon icon="mdi:text-box-outline" width="16" color="#666" />
          <span>Task Title</span>
        </div>
        <div v-if="effectiveTaskTitle" class="d-flex align-start justify-space-between ga-3">
          <div class="agm-readonly-box flex-grow-1">
            {{ effectiveTaskTitle }}
          </div>
          <v-btn
            variant="tonal"
            color="primary"
            size="small"
            class="agm-change-task-title-btn"
            @click="emit('change-task-title')"
          >
            <v-icon start size="16">mdi-pencil</v-icon>
            Change
          </v-btn>
        </div>
        <div v-else class="agm-readonly-box agm-readonly-empty">
          Waiting for task title (enter it in the chat on the left)...
        </div>
      </div>

      <!-- Entity 카드 -->
      <div v-if="entityData" class="agm-entity-card">
        <div class="d-flex align-center ga-3 justify-space-between">
          <div class="d-flex align-center ga-3">
          <div class="agm-entity-icon">
            <Icon icon="mdi:domain" width="24" color="#1B84FF" />
          </div>
          <div>
            <div class="agm-entity-name">{{ entityName }}</div>
            <div v-if="regNo" class="agm-entity-reg">Registration No: {{ regNo }}</div>
            <div v-if="articleNo" class="agm-entity-reg" :title="articleNo">
              Constitution Clause: <strong>{{ constitutionClauseNo || '—' }}</strong>
              <span class="agm-entity-article">({{ articleNo }})</span>
            </div>
          </div>
          </div>
          <v-btn
            variant="tonal"
            color="primary"
            size="small"
            class="agm-change-entity-btn"
            @click="emit('change-entity')"
          >
            <v-icon start size="16">mdi-swap-horizontal</v-icon>
            Change
          </v-btn>
        </div>
      </div>

      <div v-else class="agm-empty">
        <Icon icon="mdi:link-variant-plus" width="40" color="#d0d0d0" class="mb-2" />
        <div style="font-size: 14px; font-weight: 600; color: #666;">No entity linked</div>
        <div style="font-size: 12px; color: #999; margin-top: 4px;">
          Use the chatbot on the left to select a T-Link entity.
        </div>
      </div>

      <!-- Load Previous AGM (이전에 같은 entity 로 만든 AGM task 가 있으면) -->
      <div v-if="entityData && pastTasksLoaded && pastTasks.length > 0" class="agm-load-prev">
        <Icon icon="mdi:history" width="16" color="#5c6bc0" />
        <span class="agm-load-prev-text">
          {{ pastTasks.length }} previous AGM task{{ pastTasks.length > 1 ? 's' : '' }} found for this entity.
        </span>
        <v-btn
          size="small"
          variant="tonal"
          color="primary"
          prepend-icon="mdi-content-duplicate"
          @click="loadPrevDialog = true"
        >
          Load Previous
        </v-btn>
      </div>

      <!-- Load Previous Dialog -->
      <v-dialog v-model="loadPrevDialog" max-width="700">
        <v-card>
          <v-card-title class="d-flex align-center ga-2">
            <Icon icon="mdi:history" width="20" color="#5c6bc0" />
            <span>Load Previous AGM Task</span>
          </v-card-title>
          <v-card-text>
            <div class="text-caption text-medium-emphasis mb-3">
              Select a past AGM task — its form data will be loaded into this form.
              Existing input will be overwritten.
            </div>
            <v-list density="compact" lines="two" class="agm-prev-list">
              <v-list-item
                v-for="t in pastTasks"
                :key="t.taskId"
                @click="applyPastTask(t)"
                class="agm-prev-item"
              >
                <template #prepend>
                  <Icon icon="mdi:file-document-outline" width="18" color="#5c6bc0" />
                </template>
                <v-list-item-title class="font-weight-medium">{{ t.taskTitle || `Task #${t.taskId}` }}</v-list-item-title>
                <v-list-item-subtitle class="text-caption">
                  {{ t.taskTypeName }} • {{ formatPastDate(t.sysregdate) }}
                  <span v-if="t.sysregname"> • by {{ t.sysregname }}</span>
                  <span v-if="t.docCount > 0"> • {{ t.docCount }} doc{{ t.docCount > 1 ? 's' : '' }}</span>
                </v-list-item-subtitle>
                <template #append>
                  <v-icon size="18" color="primary">mdi-arrow-right</v-icon>
                </template>
              </v-list-item>
            </v-list>
            <div v-if="pastTasks.length === 0" class="text-center pa-4 text-medium-emphasis">
              No past AGM tasks found.
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn variant="text" @click="loadPrevDialog = false">Cancel</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Common Dates -->
      <div v-if="entityData" class="agm-section" :class="{ 'agm-section-error': hasSectionErrors('dates') }">
        <div class="agm-section-header">
          <Icon icon="mdi:calendar-multiple" width="16" color="#888" />
          <span>Dates</span>
          <div class="agm-section-docs" v-if="docsForSection('dates').length">
            <span class="agm-section-docs-label">Applies to:</span>
            <v-chip
              v-for="d in docsForSection('dates').slice(0, MAX_DOC_CHIPS)"
              :key="`dates-${d.docCode || d.templateName}`"
              size="x-small"
              variant="tonal"
              color="primary"
              class="agm-doc-chip"
            >
              {{ docLabel(d) }}
            </v-chip>
            <span v-if="docsForSection('dates').length > MAX_DOC_CHIPS" class="agm-more-chip">
              +{{ docsForSection('dates').length - MAX_DOC_CHIPS }}
            </span>
          </div>
        </div>
        <v-row dense>
          <v-col cols="6">
            <v-menu v-model="letterDateMenu" :close-on-content-click="false" location="bottom">
              <template #activator="{ props: menuProps }">
                <v-text-field
                  v-bind="menuProps"
                  v-model="letterDateDisplay"
                  :placeholder="letterDatePlaceholder"
                  label="Letter Date *"
                  :error="hasFieldError('letterDate')"
                  :error-messages="hasFieldError('letterDate') ? ['Required'] : []"
                  variant="outlined"
                  density="compact"
                  hide-details
                  readonly
                  append-inner-icon="mdi-calendar"
                />
              </template>
              <v-date-picker
                v-bind="letterDatePickerProps"
                v-model="letterDatePickerValue"
                @update:model-value="letterDateMenu = false"
              />
            </v-menu>
            <v-chip
              v-if="suggestedLetterDate && letterDate !== suggestedLetterDate && !isSuggestionDismissed('letterDate', suggestedLetterDate)"
              size="x-small" variant="outlined" color="warning"
              prepend-icon="mdi-lightbulb-outline"
              closable
              close-icon="mdi-close"
              class="mt-1 agm-suggest-chip"
              @click="letterDate = suggestedLetterDate"
              @click:close="dismissSuggestion('letterDate', suggestedLetterDate)"
            >
              Today: {{ formatIsoForDisplay(suggestedLetterDate) }}
            </v-chip>
          </v-col>
          <v-col cols="6">
            <v-menu v-model="fiscalYearEndDateMenu" :close-on-content-click="false" location="bottom">
              <template #activator="{ props: menuProps }">
                <v-text-field
                  v-bind="menuProps"
                  v-model="fiscalYearEndDateDisplay"
                  :placeholder="fiscalYearEndDatePlaceholder"
                  label="Fiscal Year End *"
                  :error="hasFieldError('fiscalYearEndDate')"
                  :error-messages="hasFieldError('fiscalYearEndDate') ? ['Required'] : []"
                  variant="outlined"
                  density="compact"
                  hide-details="auto"
                  readonly
                  append-inner-icon="mdi-calendar"
                />
              </template>
              <v-date-picker
                v-bind="fiscalYearEndDatePickerProps"
                v-model="fiscalYearEndDatePickerValue"
                @update:model-value="fiscalYearEndDateMenu = false"
              />
            </v-menu>
            <v-chip
              v-if="suggestedFiscalYearEnd && fiscalYearEndDate !== suggestedFiscalYearEnd && !isSuggestionDismissed('fiscalYearEnd', suggestedFiscalYearEnd)"
              size="x-small" variant="outlined" color="warning"
              prepend-icon="mdi-lightbulb-outline"
              closable
              close-icon="mdi-close"
              class="mt-1 agm-suggest-chip"
              @click="fiscalYearEndDate = suggestedFiscalYearEnd"
              @click:close="dismissSuggestion('fiscalYearEnd', suggestedFiscalYearEnd)"
            >
              From entity: {{ formatIsoForDisplay(suggestedFiscalYearEnd) }}
            </v-chip>
          </v-col>
          <v-col cols="12">
            <v-text-field v-model="fiscalYearEndLabel" label="Fiscal Year End Label (override)"
              placeholder="e.g. YEAR ENDED 31 DECEMBER 2025"
              variant="outlined" density="compact" hide-details="auto"
              persistent-hint hint="Click suggestion below to auto-generate from Fiscal Year End." />
            <v-chip
              v-if="suggestedFiscalYearEndLabel && fiscalYearEndLabel !== suggestedFiscalYearEndLabel && !isSuggestionDismissed('fyeLabel', suggestedFiscalYearEndLabel)"
              size="x-small" variant="outlined" color="warning"
              prepend-icon="mdi-lightbulb-outline"
              closable
              close-icon="mdi-close"
              class="mt-1 agm-suggest-chip"
              @click="fiscalYearEndLabel = suggestedFiscalYearEndLabel"
              @click:close="dismissSuggestion('fyeLabel', suggestedFiscalYearEndLabel)"
            >
              Suggest: {{ suggestedFiscalYearEndLabel }}
            </v-chip>
          </v-col>
          <v-col cols="6">
            <v-menu v-model="directorsResolutionDateMenu" :close-on-content-click="false" location="bottom">
              <template #activator="{ props: menuProps }">
                <v-text-field
                  v-bind="menuProps"
                  v-model="directorsResolutionDateDisplay"
                  :placeholder="directorsResolutionDatePlaceholder"
                  label="Directors' Resolution Date *"
                  :error="hasFieldError('directorsResolutionDate')"
                  :error-messages="hasFieldError('directorsResolutionDate') ? ['Required'] : []"
                  variant="outlined"
                  density="compact"
                  hide-details
                  readonly
                  append-inner-icon="mdi-calendar"
                />
              </template>
              <v-date-picker
                v-bind="directorsResolutionDatePickerProps"
                v-model="directorsResolutionDatePickerValue"
                @update:model-value="directorsResolutionDateMenu = false"
              />
            </v-menu>
            <v-chip
              v-if="suggestedDirectorsResolutionDate && directorsResolutionDate !== suggestedDirectorsResolutionDate && !isSuggestionDismissed('directorsResolutionDate', suggestedDirectorsResolutionDate)"
              size="x-small" variant="outlined" color="warning"
              prepend-icon="mdi-lightbulb-outline"
              closable
              close-icon="mdi-close"
              class="mt-1 agm-suggest-chip"
              @click="directorsResolutionDate = suggestedDirectorsResolutionDate"
              @click:close="dismissSuggestion('directorsResolutionDate', suggestedDirectorsResolutionDate)"
            >
              Letter − 3 days: {{ formatIsoForDisplay(suggestedDirectorsResolutionDate) }}
            </v-chip>
          </v-col>
          <v-col cols="6">
            <v-menu v-model="circulationDateMenu" :close-on-content-click="false" location="bottom">
              <template #activator="{ props: menuProps }">
                <v-text-field
                  v-bind="menuProps"
                  v-model="circulationDateDisplay"
                  :placeholder="circulationDatePlaceholder"
                  label="Circulation Date *"
                  :error="hasFieldError('circulationDate')"
                  :error-messages="hasFieldError('circulationDate') ? ['Required'] : []"
                  variant="outlined"
                  density="compact"
                  hide-details
                  readonly
                  append-inner-icon="mdi-calendar"
                />
              </template>
              <v-date-picker
                v-bind="circulationDatePickerProps"
                v-model="circulationDatePickerValue"
                @update:model-value="circulationDateMenu = false"
              />
            </v-menu>
            <v-chip
              v-if="suggestedCirculationDate && circulationDate !== suggestedCirculationDate && !isSuggestionDismissed('circulationDate', suggestedCirculationDate)"
              size="x-small" variant="outlined" color="warning"
              prepend-icon="mdi-lightbulb-outline"
              closable
              close-icon="mdi-close"
              class="mt-1 agm-suggest-chip"
              @click="circulationDate = suggestedCirculationDate"
              @click:close="dismissSuggestion('circulationDate', suggestedCirculationDate)"
            >
              Today: {{ formatIsoForDisplay(suggestedCirculationDate) }}
            </v-chip>
          </v-col>
          <v-col cols="6">
            <v-menu v-model="returnDeadlineMenu" :close-on-content-click="false" location="bottom">
              <template #activator="{ props: menuProps }">
                <v-text-field
                  v-bind="menuProps"
                  v-model="returnDeadlineDisplay"
                  :placeholder="returnDeadlinePlaceholder"
                  label="Return Deadline *"
                  :error="hasFieldError('returnDeadline')"
                  :error-messages="hasFieldError('returnDeadline') ? ['Required'] : []"
                  variant="outlined"
                  density="compact"
                  hide-details
                  readonly
                  append-inner-icon="mdi-calendar"
                />
              </template>
              <v-date-picker
                v-bind="returnDeadlinePickerProps"
                v-model="returnDeadlinePickerValue"
                @update:model-value="returnDeadlineMenu = false"
              />
            </v-menu>
            <v-chip
              v-if="suggestedReturnDeadline && returnDeadline !== suggestedReturnDeadline && !isSuggestionDismissed('returnDeadline', suggestedReturnDeadline)"
              size="x-small" variant="outlined" color="warning"
              prepend-icon="mdi-lightbulb-outline"
              closable
              close-icon="mdi-close"
              class="mt-1 agm-suggest-chip"
              @click="returnDeadline = suggestedReturnDeadline"
              @click:close="dismissSuggestion('returnDeadline', suggestedReturnDeadline)"
            >
              Circulation + 14 days: {{ formatIsoForDisplay(suggestedReturnDeadline) }}
            </v-chip>
          </v-col>
          <v-col cols="6">
            <v-menu v-model="certificationDateMenu" :close-on-content-click="false" location="bottom">
              <template #activator="{ props: menuProps }">
                <v-text-field
                  v-bind="menuProps"
                  v-model="certificationDateDisplay"
                  :placeholder="certificationDatePlaceholder"
                  label="Certification Date *"
                  :error="hasFieldError('certificationDate')"
                  :error-messages="hasFieldError('certificationDate') ? ['Required'] : []"
                  variant="outlined"
                  density="compact"
                  hide-details
                  readonly
                  append-inner-icon="mdi-calendar"
                />
              </template>
              <v-date-picker
                v-bind="certificationDatePickerProps"
                v-model="certificationDatePickerValue"
                @update:model-value="certificationDateMenu = false"
              />
            </v-menu>
            <v-chip
              v-if="suggestedCertificationDate && certificationDate !== suggestedCertificationDate && !isSuggestionDismissed('certificationDate', suggestedCertificationDate)"
              size="x-small" variant="outlined" color="warning"
              prepend-icon="mdi-lightbulb-outline"
              closable
              close-icon="mdi-close"
              class="mt-1 agm-suggest-chip"
              @click="certificationDate = suggestedCertificationDate"
              @click:close="dismissSuggestion('certificationDate', suggestedCertificationDate)"
            >
              Same as Circulation: {{ formatIsoForDisplay(suggestedCertificationDate) }}
            </v-chip>
          </v-col>
        </v-row>
      </div>

      <!-- Resolution -->
      <div v-if="entityData" class="agm-section" :class="{ 'agm-section-error': hasSectionErrors('resolution') }">
        <div class="agm-section-header">
          <Icon icon="mdi:gavel" width="16" color="#888" />
          <span>Resolution & Signatories</span>
          <div class="agm-section-docs" v-if="docsForSection('resolution').length">
            <span class="agm-section-docs-label">Applies to:</span>
            <v-chip
              v-for="d in docsForSection('resolution').slice(0, MAX_DOC_CHIPS)"
              :key="`res-${d.docCode || d.templateName}`"
              size="x-small"
              variant="tonal"
              color="primary"
              class="agm-doc-chip"
            >
              {{ docLabel(d) }}
            </v-chip>
            <span v-if="docsForSection('resolution').length > MAX_DOC_CHIPS" class="agm-more-chip">
              +{{ docsForSection('resolution').length - MAX_DOC_CHIPS }}
            </span>
          </div>
        </div>
        <v-row dense>
          <v-col cols="6">
            <div class="agm-drop-wrap">
              <v-text-field v-model="statutoryDeclarantName" label="Statutory Declarant Name *"
                :error="hasFieldError('statutoryDeclarantName')"
                :error-messages="hasFieldError('statutoryDeclarantName') ? ['Required'] : []"
                variant="outlined" density="compact" hide-details="auto">
                <template #append-inner>
                  <v-tooltip location="top" :open-delay="150">
                    <template #activator="{ props: tProps }">
                      <v-btn v-bind="tProps" icon="mdi-creation" size="x-small" variant="text" color="primary"
                        @click="requestLookup({ fieldGroup: 'statutoryDeclarant', defaultRole: 'DIRECTOR' })" />
                    </template>
                    <span>Lookup person from chat</span>
                  </v-tooltip>
                </template>
              </v-text-field>
              <div v-if="isDragging" class="agm-drop-overlay"
                @dragover.prevent
                @drop.prevent="handleFieldDrop($event, (v) => statutoryDeclarantName = v)" />
            </div>
            <v-chip
              v-if="suggestedDeclarantName && statutoryDeclarantName !== suggestedDeclarantName && !isSuggestionDismissed('declarant', suggestedDeclarantName)"
              size="x-small" variant="outlined" color="warning"
              prepend-icon="mdi-lightbulb-outline"
              closable close-icon="mdi-close"
              class="mt-1 agm-suggest-chip"
              @click="statutoryDeclarantName = suggestedDeclarantName"
              @click:close="dismissSuggestion('declarant', suggestedDeclarantName)"
            >
              First Director: {{ suggestedDeclarantName }}
            </v-chip>
          </v-col>
          <v-col cols="6">
            <div class="agm-drop-wrap">
              <v-text-field v-model="certifyingSecretaryName" label="Certifying Secretary Name *"
                :error="hasFieldError('certifyingSecretaryName')"
                :error-messages="hasFieldError('certifyingSecretaryName') ? ['Required'] : []"
                variant="outlined" density="compact" hide-details="auto">
                <template #append-inner>
                  <v-tooltip location="top" :open-delay="150">
                    <template #activator="{ props: tProps }">
                      <v-btn v-bind="tProps" icon="mdi-creation" size="x-small" variant="text" color="primary"
                        @click="requestLookup({ fieldGroup: 'certifyingSecretary', defaultRole: 'SECRETARY' })" />
                    </template>
                    <span>Lookup person from chat</span>
                  </v-tooltip>
                </template>
              </v-text-field>
              <div v-if="isDragging" class="agm-drop-overlay"
                @dragover.prevent
                @drop.prevent="handleFieldDrop($event, (v) => certifyingSecretaryName = v)" />
            </div>
            <v-chip
              v-if="suggestedSecretaryName && certifyingSecretaryName !== suggestedSecretaryName && !isSuggestionDismissed('secretary', suggestedSecretaryName)"
              size="x-small" variant="outlined" color="warning"
              prepend-icon="mdi-lightbulb-outline"
              closable close-icon="mdi-close"
              class="mt-1 agm-suggest-chip"
              @click="certifyingSecretaryName = suggestedSecretaryName"
              @click:close="dismissSuggestion('secretary', suggestedSecretaryName)"
            >
              {{ secretariesList.length === 1 ? 'Only Secretary' : 'First Secretary' }}: {{ suggestedSecretaryName }}
            </v-chip>
          </v-col>
          <v-col v-if="docFlags.hasCertifiedExtract" cols="12">
            <v-textarea v-model="resolutionText" label="Resolution Text"
              rows="3" auto-grow variant="outlined" density="compact" hide-details />
          </v-col>
        </v-row>
      </div>

      <!-- Conditions -->
      <div v-if="entityData" class="agm-section" :class="{ 'agm-section-error': hasSectionErrors('conditions') }">
        <div class="agm-section-header">
          <Icon icon="mdi:tune-vertical" width="16" color="#888" />
          <span>Conditions</span>
          <div class="agm-section-docs" v-if="docsForSection('conditions').length">
            <span class="agm-section-docs-label">Applies to:</span>
            <v-chip
              v-for="d in docsForSection('conditions').slice(0, MAX_DOC_CHIPS)"
              :key="`cond-${d.docCode || d.templateName}`"
              size="x-small"
              variant="tonal"
              color="primary"
              class="agm-doc-chip"
            >
              {{ docLabel(d) }}
            </v-chip>
            <span v-if="docsForSection('conditions').length > MAX_DOC_CHIPS" class="agm-more-chip">
              +{{ docsForSection('conditions').length - MAX_DOC_CHIPS }}
            </span>
          </div>
        </div>
        <v-row dense>
          <v-col cols="4">
            <v-text-field v-model.number="directorCount" type="number" min="1" label="Director Count"
              variant="outlined" density="compact" hide-details="auto"
              persistent-hint hint="If set to 1, 'sole director' wording will be used." />
          </v-col>
          <v-col cols="8">
            <v-radio-group v-model="resolutionBasis" inline hide-details density="compact"
              @update:model-value="constitutionClauseNo = ''"
              :disabled="entityClauseRegistered" class="agm-resolution-basis">
              <template #label>
                <span class="text-caption text-medium-emphasis">
                  Resolution basis
                  <v-icon v-if="entityClauseRegistered" size="12" class="ml-1">mdi-lock</v-icon>
                  <span v-if="entityClauseRegistered" class="ml-1">(registered in entity)</span>
                </span>
              </template>
              <v-radio label="Constitution (Clause)" value="constitution" color="primary" />
              <v-radio label="Articles of Association (Article)" value="articles" color="primary" />
              <v-radio label="Third Schedule (no constitution)" value="third_schedule" color="primary" />
            </v-radio-group>
          </v-col>
          <v-col cols="4">
            <!-- Third Schedule: 조항 불필요 -->
            <v-text-field v-if="resolutionBasis === 'third_schedule'"
              model-value="N/A" disabled label="Constitution Clause No"
              variant="outlined" density="compact" hide-details="auto" />
            <!-- Entity 에 등록된 정관 조항 → readonly (변경 불가) -->
            <v-text-field v-else-if="entityClauseRegistered"
              v-model="constitutionClauseNo"
              :label="resolutionBasis === 'articles' ? 'Article No' : 'Constitution Clause No'"
              readonly append-inner-icon="mdi-lock"
              variant="outlined" density="compact" hide-details="auto"
              persistent-hint hint="Registered in entity (locked)" />
            <!-- 미등록 → select box 로 선택 -->
            <v-select v-else
              v-model="constitutionClauseModel"
              :items="filteredClauseOptions"
              :label="resolutionBasis === 'articles' ? 'Article No' : 'Constitution Clause No'"
              placeholder="Select clause number"
              :error="hasFieldError('constitutionClauseNo')"
              :error-messages="hasFieldError('constitutionClauseNo') ? ['Required'] : []"
              variant="outlined" density="compact" hide-details="auto" clearable />
          </v-col>
          <!-- Has Dividend toggle — preset 에 배당 문서가 있을 때만 노출 -->
          <v-col v-if="docFlags.hasDividend" cols="4" class="d-flex align-center">
            <v-checkbox v-model="hasDividend" label="Has Dividend"
              hide-details density="compact" color="primary"
              persistent-hint hint="Show dividend form & generate Declaration of Dividend doc" />
          </v-col>
        </v-row>
      </div>

      <!-- Audited 전용: Auditor -->
      <div v-if="entityData && docFlags.isAudited" class="agm-section" :class="{ 'agm-section-error': hasSectionErrors('auditor') }">
        <div class="agm-section-header">
          <Icon icon="mdi:shield-check-outline" width="16" color="#888" />
          <span>Auditor</span>
          <div class="agm-section-docs" v-if="docsForSection('auditor').length">
            <span class="agm-section-docs-label">Applies to:</span>
            <v-chip
              v-for="d in docsForSection('auditor').slice(0, MAX_DOC_CHIPS)"
              :key="`aud-${d.docCode || d.templateName}`"
              size="x-small"
              variant="tonal"
              color="primary"
              class="agm-doc-chip"
            >
              {{ docLabel(d) }}
            </v-chip>
            <span v-if="docsForSection('auditor').length > MAX_DOC_CHIPS" class="agm-more-chip">
              +{{ docsForSection('auditor').length - MAX_DOC_CHIPS }}
            </span>
          </div>
        </div>
        <v-row dense>
          <v-col cols="12">
            <div class="agm-drop-wrap">
              <v-text-field v-model="auditorFirmName" label="Auditor Firm Name *"
                :error="hasFieldError('auditorFirmName')"
                :error-messages="hasFieldError('auditorFirmName') ? ['Required'] : []"
                variant="outlined" density="compact" hide-details="auto">
                <template #append-inner>
                  <v-tooltip location="top" :open-delay="150">
                    <template #activator="{ props: tProps }">
                      <v-btn
                        v-bind="tProps"
                        icon="mdi-creation"
                        size="x-small"
                        variant="text"
                        color="primary"
                        @click="requestLookup({ fieldGroup: 'auditor', defaultRole: null })"
                      />
                    </template>
                    <span>Lookup person from chat (Auditor)</span>
                  </v-tooltip>
                </template>
              </v-text-field>
              <div v-if="isDragging" class="agm-drop-overlay"
                @dragover.prevent
                @drop.prevent="handleFieldDrop($event, (v) => auditorFirmName = v)" />
            </div>
          </v-col>
          <v-col cols="12">
            <div class="agm-drop-wrap">
              <v-textarea v-model="auditorAddress" label="Auditor Address"
                rows="2" auto-grow variant="outlined" density="compact" hide-details>
                <template #append-inner>
                  <v-tooltip location="top" :open-delay="150">
                    <template #activator="{ props: tProps }">
                      <v-btn
                        v-bind="tProps"
                        icon="mdi-creation"
                        size="x-small"
                        variant="text"
                        color="primary"
                        @click="requestLookup({ fieldGroup: 'auditorAddress', defaultRole: null })"
                      />
                    </template>
                    <span>Lookup address from chat (주소만)</span>
                  </v-tooltip>
                </template>
              </v-textarea>
              <div v-if="isDragging" class="agm-drop-overlay"
                @dragover.prevent
                @drop.prevent="handleFieldDrop($event, (v) => auditorAddress = v)" />
            </div>
          </v-col>
        </v-row>
      </div>

      <!-- Audit Exemption 전용: Lodger -->
      <div v-if="entityData && docFlags.hasAuditExemption" class="agm-section" :class="{ 'agm-section-error': hasSectionErrors('lodger') }">
        <div class="agm-section-header">
          <Icon icon="mdi:account-tie-outline" width="16" color="#888" />
          <span>Lodger (Audit Exemption Certificate)</span>
          <div class="agm-section-docs" v-if="docsForSection('lodger').length">
            <span class="agm-section-docs-label">Applies to:</span>
            <v-chip
              v-for="d in docsForSection('lodger').slice(0, MAX_DOC_CHIPS)"
              :key="`lod-${d.docCode || d.templateName}`"
              size="x-small"
              variant="tonal"
              color="primary"
              class="agm-doc-chip"
            >
              {{ docLabel(d) }}
            </v-chip>
            <span v-if="docsForSection('lodger').length > MAX_DOC_CHIPS" class="agm-more-chip">
              +{{ docsForSection('lodger').length - MAX_DOC_CHIPS }}
            </span>
          </div>
        </div>
        <v-row dense>
          <v-col cols="6">
            <div class="agm-drop-wrap">
              <v-text-field v-model="lodgerName" label="Lodger Name *"
                :error="hasFieldError('lodgerName')"
                :error-messages="hasFieldError('lodgerName') ? ['Required'] : []"
                variant="outlined" density="compact" hide-details="auto">
                <template #append-inner>
                  <v-tooltip location="top" :open-delay="150">
                    <template #activator="{ props: tProps }">
                      <v-btn v-bind="tProps" icon="mdi-creation" size="x-small" variant="text" color="primary"
                        @click="requestLookup({ fieldGroup: 'lodger', defaultRole: null })" />
                    </template>
                    <span>Lookup person from chat (Director or Secretary)</span>
                  </v-tooltip>
                </template>
              </v-text-field>
              <div v-if="isDragging" class="agm-drop-overlay"
                @dragover.prevent
                @drop.prevent="handleFieldDrop($event, (v) => lodgerName = v)" />
            </div>
          </v-col>
          <v-col cols="6">
            <div class="agm-drop-wrap">
              <v-text-field v-model="lodgerNric" label="Lodger NRIC *"
                :error="hasFieldError('lodgerNric')"
                :error-messages="hasFieldError('lodgerNric') ? ['Required'] : []"
                variant="outlined" density="compact" hide-details="auto" />
              <div v-if="isDragging" class="agm-drop-overlay"
                @dragover.prevent
                @drop.prevent="handleFieldDrop($event, (v) => lodgerNric = v)" />
            </div>
          </v-col>
          <v-col cols="12">
            <div class="agm-drop-wrap">
              <v-textarea v-model="lodgerAddress" label="Lodger Address *"
                :error="hasFieldError('lodgerAddress')"
                :error-messages="hasFieldError('lodgerAddress') ? ['Required'] : []"
                rows="2" auto-grow variant="outlined" density="compact" hide-details="auto" />
              <div v-if="isDragging" class="agm-drop-overlay"
                @dragover.prevent
                @drop.prevent="handleFieldDrop($event, (v) => lodgerAddress = v)" />
            </div>
          </v-col>
          <v-col cols="6">
            <div class="agm-drop-wrap">
              <v-text-field v-model="lodgerPhone" label="Lodger Phone *"
                :error="hasFieldError('lodgerPhone')"
                :error-messages="hasFieldError('lodgerPhone') ? ['Required'] : []"
                variant="outlined" density="compact" hide-details="auto" />
              <div v-if="isDragging" class="agm-drop-overlay"
                @dragover.prevent
                @drop.prevent="handleFieldDrop($event, (v) => lodgerPhone = v)" />
            </div>
          </v-col>
          <v-col cols="6">
            <div class="agm-drop-wrap">
              <v-text-field v-model="lodgerEmail" type="email" label="Lodger Email *"
                :error="hasFieldError('lodgerEmail')"
                :error-messages="hasFieldError('lodgerEmail') ? ['Required'] : []"
                variant="outlined" density="compact" hide-details="auto" />
              <div v-if="isDragging" class="agm-drop-overlay"
                @dragover.prevent
                @drop.prevent="handleFieldDrop($event, (v) => lodgerEmail = v)" />
            </div>
          </v-col>
        </v-row>
      </div>

      <!-- Dividend (사용자가 Has Dividend 체크해야 노출) -->
      <div v-if="entityData && docFlags.hasDividend && hasDividend" class="agm-section" :class="{ 'agm-section-error': hasSectionErrors('dividend') }">
        <div class="agm-section-header">
          <Icon icon="mdi:cash-multiple" width="16" color="#888" />
          <span>Dividend</span>
          <div class="agm-section-docs" v-if="docsForSection('dividend').length">
            <span class="agm-section-docs-label">Applies to:</span>
            <v-chip
              v-for="d in docsForSection('dividend').slice(0, MAX_DOC_CHIPS)"
              :key="`div-${d.docCode || d.templateName}`"
              size="x-small"
              variant="tonal"
              color="primary"
              class="agm-doc-chip"
            >
              {{ docLabel(d) }}
            </v-chip>
            <span v-if="docsForSection('dividend').length > MAX_DOC_CHIPS" class="agm-more-chip">
              +{{ docsForSection('dividend').length - MAX_DOC_CHIPS }}
            </span>
          </div>
        </div>
        <v-row dense>
          <v-col cols="4">
            <v-select v-model="dividendType" :items="dividendTypeOptions" item-title="text" item-value="value"
              label="Dividend Type *"
              :error="hasFieldError('dividendType')"
              :error-messages="hasFieldError('dividendType') ? ['Required'] : []"
              variant="outlined" density="compact" hide-details="auto" />
          </v-col>
          <v-col cols="4">
            <v-text-field v-model="dividendPerShare" type="number" step="0.01" label="Per Share (RM) *"
              :error="hasFieldError('dividendPerShare')"
              :error-messages="hasFieldError('dividendPerShare') ? ['Required'] : []"
              variant="outlined" density="compact" hide-details="auto" />
          </v-col>
          <v-col cols="4">
            <v-text-field v-model="dividendTotalAmount" type="number" step="0.01" label="Total Amount (RM) *"
              :error="hasFieldError('dividendTotalAmount')"
              :error-messages="hasFieldError('dividendTotalAmount') ? ['Required'] : []"
              variant="outlined" density="compact" hide-details="auto" />
          </v-col>
          <v-col cols="6">
            <v-text-field v-model="dividendRecordDate" type="date" label="Record Date *"
              :error="hasFieldError('dividendRecordDate')"
              :error-messages="hasFieldError('dividendRecordDate') ? ['Required'] : []"
              variant="outlined" density="compact" hide-details="auto" />
          </v-col>
          <v-col cols="6">
            <v-text-field v-model="dividendPaymentDate" type="date" label="Payment Date *"
              :error="hasFieldError('dividendPaymentDate')"
              :error-messages="hasFieldError('dividendPaymentDate') ? ['Required'] : []"
              variant="outlined" density="compact" hide-details="auto" />
          </v-col>
        </v-row>
      </div>

      <!-- Remuneration -->
      <div v-if="entityData && docFlags.hasRemuneration" class="agm-section" :class="{ 'agm-section-error': hasSectionErrors('remuneration') }">
        <div class="agm-section-header">
          <Icon icon="mdi:wallet-outline" width="16" color="#888" />
          <span>Directors' Remuneration</span>
          <div class="agm-section-docs" v-if="docsForSection('remuneration').length">
            <span class="agm-section-docs-label">Applies to:</span>
            <v-chip
              v-for="d in docsForSection('remuneration').slice(0, MAX_DOC_CHIPS)"
              :key="`rem-${d.docCode || d.templateName}`"
              size="x-small"
              variant="tonal"
              color="primary"
              class="agm-doc-chip"
            >
              {{ docLabel(d) }}
            </v-chip>
            <span v-if="docsForSection('remuneration').length > MAX_DOC_CHIPS" class="agm-more-chip">
              +{{ docsForSection('remuneration').length - MAX_DOC_CHIPS }}
            </span>
          </div>
        </div>
        <v-row dense>
          <v-col cols="6">
            <v-text-field v-model="directorsRemunerationTotal" type="number" step="0.01"
              label="Total Remuneration (RM) *"
              :error="hasFieldError('directorsRemunerationTotal')"
              :error-messages="hasFieldError('directorsRemunerationTotal') ? ['Required'] : []"
              variant="outlined" density="compact" hide-details="auto" />
          </v-col>
        </v-row>
      </div>

      <!-- Retiring Directors (loop) -->
      <div v-if="entityData && docFlags.hasAdoption" class="agm-section">
        <div class="agm-section-header">
          <Icon icon="mdi:account-arrow-right-outline" width="16" color="#888" />
          <span>Retiring Directors (Re-election)</span>
          <div class="agm-section-docs" v-if="docsForSection('retiringDirectors').length">
            <span class="agm-section-docs-label">Applies to:</span>
            <v-chip
              v-for="d in docsForSection('retiringDirectors').slice(0, MAX_DOC_CHIPS)"
              :key="`rd-${d.docCode || d.templateName}`"
              size="x-small"
              variant="tonal"
              color="primary"
              class="agm-doc-chip"
            >
              {{ docLabel(d) }}
            </v-chip>
            <span v-if="docsForSection('retiringDirectors').length > MAX_DOC_CHIPS" class="agm-more-chip">
              +{{ docsForSection('retiringDirectors').length - MAX_DOC_CHIPS }}
            </span>
          </div>
          <v-select
            v-if="electionArticleOptions.length > 0"
            v-model="electionArticleSelected"
            :items="['', ...electionArticleOptions]"
            label="Re-election Regulation (optional)"
            variant="outlined"
            density="compact"
            hide-details
            class="ml-4"
            style="max-width: 320px;"
          />
          <v-btn variant="text" size="x-small" color="primary" class="ml-auto" @click="addRetiringDirector">
            <Icon icon="mdi:plus" width="14" /> Add
          </v-btn>
        </div>
        <div v-if="!retiringDirectors.length" class="agm-loop-empty">No retiring directors added.</div>
        <div v-for="(rd, i) in retiringDirectors" :key="i" class="agm-loop-row">
          <v-row dense>
            <v-col cols="5">
              <div class="agm-drop-wrap">
                <v-text-field v-model="rd.name" label="Director Name"
                  variant="outlined" density="compact" hide-details>
                  <template #append-inner>
                    <v-tooltip location="top" :open-delay="150">
                      <template #activator="{ props: tProps }">
                        <v-btn v-bind="tProps" icon="mdi-creation" size="x-small" variant="text" color="primary"
                          @click="requestLookup({ fieldGroup: 'retiringDirector', index: i, defaultRole: 'DIRECTOR' })" />
                      </template>
                      <span>Lookup director from chat</span>
                    </v-tooltip>
                  </template>
                </v-text-field>
                <div v-if="isDragging" class="agm-drop-overlay"
                  @dragover.prevent
                  @drop.prevent="handleFieldDrop($event, (v) => rd.name = v)" />
              </div>
            </v-col>
            <v-col cols="6">
              <div class="agm-drop-wrap">
                <v-combobox v-model="rd.regulation" :items="regulationOptions" label="Regulation"
                  placeholder="e.g. Regulation 84"
                  variant="outlined" density="compact" hide-details clearable />
                <div v-if="isDragging" class="agm-drop-overlay"
                  @dragover.prevent
                  @drop.prevent="handleFieldDrop($event, (v) => rd.regulation = v)" />
              </div>
            </v-col>
            <v-col cols="1" class="d-flex align-center">
              <v-btn variant="text" size="small" icon @click="removeRetiringDirector(i)">
                <Icon icon="mdi:close" width="16" />
              </v-btn>
            </v-col>
          </v-row>
        </div>
      </div>

      <!-- Members (loop) — Cover Letter / FS Receipt 가 있을 때 -->
      <div v-if="entityData && (docFlags.hasCoverLetter || docFlags.hasFsReceipt)" class="agm-section">
        <div class="agm-section-header">
          <Icon icon="mdi:account-multiple-outline" width="16" color="#888" />
          <span>Members (Recipients)</span>
          <div class="agm-section-docs" v-if="docsForSection('members').length">
            <span class="agm-section-docs-label">Applies to:</span>
            <v-chip
              v-for="d in docsForSection('members').slice(0, MAX_DOC_CHIPS)"
              :key="`mem-${d.docCode || d.templateName}`"
              size="x-small"
              variant="tonal"
              color="primary"
              class="agm-doc-chip"
            >
              {{ docLabel(d) }}
            </v-chip>
            <span v-if="docsForSection('members').length > MAX_DOC_CHIPS" class="agm-more-chip">
              +{{ docsForSection('members').length - MAX_DOC_CHIPS }}
            </span>
          </div>
          <v-btn variant="text" size="x-small" color="primary" class="ml-auto" @click="addMember">
            <Icon icon="mdi:plus" width="14" /> Add
          </v-btn>
        </div>
        <div v-if="!members.length" class="agm-loop-empty">No members added.</div>
        <div v-for="(m, i) in members" :key="i" class="agm-loop-row">
          <v-row dense>
            <v-col cols="4">
              <div class="agm-drop-wrap">
                <v-text-field v-model="m.name" label="Member Name"
                  variant="outlined" density="compact" hide-details>
                  <template #append-inner>
                    <v-tooltip location="top" :open-delay="150">
                      <template #activator="{ props: tProps }">
                        <v-btn v-bind="tProps" icon="mdi-creation" size="x-small" variant="text" color="primary"
                          @click="requestLookup({ fieldGroup: 'member', index: i, defaultRole: 'SHAREHOLDER' })" />
                      </template>
                      <span>Lookup member from chat</span>
                    </v-tooltip>
                  </template>
                </v-text-field>
                <div v-if="isDragging" class="agm-drop-overlay"
                  @dragover.prevent
                  @drop.prevent="handleFieldDrop($event, (v) => m.name = v)" />
              </div>
            </v-col>
            <v-col cols="7">
              <div class="agm-drop-wrap">
                <v-text-field v-model="m.address" label="Member Address"
                  variant="outlined" density="compact" hide-details>
                  <template v-if="(m.addresses?.length || 0) > 1" #append-inner>
                    <v-tooltip location="top" :open-delay="150">
                      <template #activator="{ props: tProps }">
                        <v-btn v-bind="tProps" icon="mdi-creation" size="x-small" variant="text" color="primary"
                          @click="requestAddressChoice(i)" />
                      </template>
                      <span>Choose from multiple addresses</span>
                    </v-tooltip>
                  </template>
                </v-text-field>
                <div v-if="isDragging" class="agm-drop-overlay"
                  @dragover.prevent
                  @drop.prevent="handleFieldDrop($event, (v) => m.address = v)" />
              </div>
              <!-- 주소 후보 칩 — 클릭으로 선택 -->
              <div v-if="(m.addresses?.length ?? 0) >= 1" class="agm-addr-suggest">
                <v-chip
                  v-for="(addr, ai) in m.addresses"
                  :key="`m-addr-${i}-${ai}`"
                  size="x-small"
                  variant="tonal"
                  :color="m.address === addr.value ? 'primary' : 'default'"
                  @click="m.address = addr.value"
                >
                  {{ addressSuggestionLabel(addr, ai, m.addresses) }}
                </v-chip>
              </div>
            </v-col>
            <v-col cols="1" class="d-flex align-center">
              <v-btn variant="text" size="small" icon @click="removeMember(i)">
                <Icon icon="mdi:close" width="16" />
              </v-btn>
            </v-col>
          </v-row>
        </div>
      </div>

      <!-- 문서 목록 -->
      <div v-if="entityData" class="agm-section">
        <div class="agm-section-header">
          <Icon icon="mdi:file-document-multiple-outline" width="16" color="#5c6bc0" />
          <span>
            Documents
            <template v-if="skippedDocsCount > 0">
              ({{ selection.documents.length - skippedDocsCount }} of {{ selection.documents.length }})
            </template>
            <template v-else>
              ({{ selection.documents.length }})
            </template>
          </span>
          <v-chip v-if="skippedDocsCount > 0" size="x-small" color="warning" variant="tonal" class="ml-2">
            <v-icon start size="12">mdi-cancel</v-icon>
            {{ skippedDocsCount }} skipped
          </v-chip>
        </div>
        <div
          v-for="(doc, idx) in selection.documents"
          :key="doc.docCode"
          class="agm-doc-item"
          :class="{ 'agm-doc-item--skipped': isDocSkipped(doc.templateName).skipped }"
        >
          <div class="agm-doc-num">{{ idx + 1 }}</div>
          <Icon icon="mdi:file-document-outline" width="16" color="#5c6bc0" />
          <span class="agm-doc-name">{{ doc.templateName }}</span>
          <v-chip
            v-if="isDocSkipped(doc.templateName).skipped"
            size="x-small"
            color="warning"
            variant="tonal"
            class="agm-doc-skip-badge"
            :title="isDocSkipped(doc.templateName).reason"
          >
            <v-icon start size="12">mdi-cancel</v-icon>
            Skipped — {{ isDocSkipped(doc.templateName).reason }}
          </v-chip>
          <span class="agm-doc-code">{{ doc.docCode }}</span>
        </div>
      </div>

      <!-- Bulk signer picker (only when chosen) -->
      <div v-if="entityData && signerAssignMode === 'bulk' && showBulkSignerPanel" class="agm-section">
        <div class="agm-section-header">
          <Icon icon="mdi:account-multiple-check-outline" width="16" color="#888" />
          <span>Signers (Apply to all documents)</span>
          <v-chip v-if="bulkSigners.length" size="x-small" color="success" variant="tonal" class="ml-2">
            {{ bulkSigners.length }} selected
          </v-chip>
        </div>
        <SignerListField
          :model-value="bulkSigners"
          label="Signers"
          var-key="signers"
          :entity-cd-override="icpCd"
          @update="onBulkSignerFieldUpdate"
        />
        <div class="d-flex justify-end mt-3">
          <v-btn
            variant="flat"
            color="primary"
            size="small"
            :disabled="bulkSigners.length === 0"
            @click="handleCreateTask"
          >
            Continue
          </v-btn>
        </div>
      </div>
    </div>

    <div v-if="progress" class="agm-progress">
      <v-progress-linear v-if="creating" indeterminate color="primary" height="4" rounded />
      <div class="agm-progress-text" :class="{ 'agm-progress-text--error': progress.startsWith('Error:') }">
        {{ progress }}
      </div>
      <div v-if="showValidationErrors && missingRequired.length > 0" class="agm-missing-list">
        <div class="agm-missing-title-row">
          <div class="agm-missing-title">Missing required fields</div>
          <v-btn
            icon="mdi-close"
            size="x-small"
            variant="text"
            color="error"
            title="Close"
            @click="showValidationErrors = false"
          />
        </div>
        <ul class="agm-missing-items">
          <li v-for="m in missingRequired" :key="m.key">{{ m.label }}</li>
        </ul>
      </div>
    </div>

    <div class="agm-footer">
      <v-btn variant="outlined" color="grey" size="small" @click="emit('back')">
        <Icon icon="mdi:arrow-left" width="16" class="mr-1" /> Back
      </v-btn>
      <v-tooltip :disabled="!(editMode && !isDirty)" location="top" text="No changes to update">
        <template #activator="{ props: tProps }">
          <span v-bind="tProps">
            <v-btn
              variant="flat"
              color="primary"
              size="small"
              :prepend-icon="editMode ? 'mdi-content-save' : 'mdi-check'"
              :disabled="creating || (editMode && !isDirty)"
              :loading="creating"
              @click="onCreateTaskClick"
            >
              {{ editMode ? 'Update Task' : 'Create Task' }}
            </v-btn>
          </span>
        </template>
      </v-tooltip>
    </div>

    <!-- Signer assignment choice dialog -->
    <!-- Disabled temporarily by request -->
    <!--
    <v-dialog v-model="signerAssignDialog" max-width="520">
      <v-card>
        <v-card-title class="text-h6">Assign signers</v-card-title>
        <v-card-text>
          Would you like to assign signers in bulk for all documents, or assign per document?
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="outlined" @click="chooseSignerMode('perDoc')">Per document</v-btn>
          <v-btn color="primary" variant="flat" @click="chooseSignerMode('bulk')">Bulk</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    -->
  </div>
</template>

<style scoped>
.agm-panel { height: 100%; display: flex; flex-direction: column; overflow: hidden; }
.agm-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 20px 24px 12px; border-bottom: 1px solid #f0f0f0;
}
.agm-title { font-size: 18px; font-weight: 700; color: #1a1a2e; }
.agm-subtitle { font-size: 12px; color: #888; margin-top: 2px; }
.agm-content { flex: 1; overflow-y: auto; padding: 20px 24px; }
.agm-entity-card {
  background: #f0f5ff; border: 1px solid #d6e4ff; border-radius: 12px;
  padding: 16px; margin-bottom: 20px;
}

.agm-change-entity-btn {
  align-self: flex-start;
}
.agm-entity-icon {
  width: 40px; height: 40px; border-radius: 10px; background: #fff;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
}
.agm-entity-name { font-size: 15px; font-weight: 600; color: #1a1a2e; }
.agm-entity-reg { font-size: 12px; color: #666; margin-top: 1px; }
.agm-entity-article {
  color: #999; margin-left: 4px; font-size: 11px;
  display: inline-block; max-width: 280px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  vertical-align: middle;
}
.agm-empty {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 28px 16px; background: #fafafa; border: 1px dashed #e0e0e0; border-radius: 12px;
  margin-bottom: 20px; text-align: center;
}
.agm-section {
  margin-bottom: 16px;
  background: #ffffff;
  border: 1px solid #e9edf5;
  border-radius: 12px;
  padding: 0 0 12px 0;     /* 헤더가 가로 폭 100% 채우도록 패딩 제거 */
  overflow: hidden;          /* 헤더의 둥근 모서리 잘리지 않게 */
}
.agm-section > :not(.agm-section-header) {
  padding-left: 14px;
  padding-right: 14px;
}
.agm-section-header {
  display: flex; align-items: center; gap: 6px;
  font-size: 12px; font-weight: 600; color: #283593;
  margin: 0 0 12px 0;
  padding: 10px 14px;
  background: linear-gradient(180deg, #eef2ff 0%, #e8ecfb 100%);
  border-bottom: 1px solid #d6dcf2;
}
.agm-section-docs {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: 10px;
  flex-wrap: wrap;
}
.agm-section-docs-label {
  font-size: 11px;
  color: #888;
  font-weight: 500;
}
.agm-doc-chip {
  max-width: 240px;
}
.agm-doc-chip :deep(.v-chip__content) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.agm-more-chip {
  font-size: 11px;
  color: #888;
}
.agm-suggest-chip {
  cursor: pointer;
  font-size: 11px !important;
  background: #ffffff !important;
  color: #212121 !important;
  border-color: #ffb300 !important;     /* warning yellow border */
  border-width: 1px !important;
}
.agm-suggest-chip:hover {
  background: #fff8e1 !important;        /* very light yellow tint on hover */
}
.agm-suggest-chip :deep(.v-chip__content) {
  color: #212121 !important;             /* 본문 검정 */
}
.agm-suggest-chip :deep(.v-icon) {
  color: #ffb300 !important;             /* lightbulb 아이콘만 노랑 강조 */
}
.agm-suggest-chip :deep(.v-chip__close) {
  font-size: 13px !important;
  margin-inline-start: 4px;
  opacity: 0.7;
  color: #757575 !important;             /* X 는 회색 */
}
.agm-suggest-chip :deep(.v-chip__close:hover) {
  opacity: 1;
  color: #212121 !important;
}
/* 긴 주소 텍스트 — chip 안에서 한 줄로 ellipsis */
.agm-suggest-chip--ellipsis {
  max-width: 100%;
}
.agm-suggest-chip--ellipsis :deep(.v-chip__content) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: calc(100% - 8px);
}
.agm-suggest-chip-count {
  margin-left: 6px;
  font-size: 10px;
  color: #757575;
  font-style: italic;
}
/* v-tooltip 안의 주소 본문 — 줄바꿈 허용해서 전체 가독성 확보 */
.agm-suggest-tooltip {
  font-size: 12px;
  line-height: 1.5;
  white-space: normal;
  word-break: break-word;
}
.agm-suggest-tooltip-label {
  font-size: 10px;
  font-weight: 600;
  color: #ffe082;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 2px;
}
.agm-suggest-tooltip-hint {
  font-size: 10px;
  color: #bdbdbd;
  margin-top: 4px;
  font-style: italic;
}
.agm-readonly-box {
  background: #fafafa; border: 1px solid #eee; border-radius: 6px;
  padding: 10px 12px; font-size: 13px; color: #333; line-height: 1.5;
}
.agm-change-task-title-btn {
  align-self: flex-start;
}
.agm-readonly-empty { color: #bbb; font-style: italic; }
.agm-loop-empty {
  font-size: 12px; color: #aaa; padding: 8px 0; font-style: italic;
}
.agm-loop-row {
  padding: 6px 0;
  border-bottom: 1px dashed #f0f0f0;
}
.agm-loop-row:last-child { border-bottom: none; }
.agm-doc-item {
  display: flex; align-items: center; gap: 8px;
  padding: 6px 0; border-bottom: 1px dashed #f5f5f5;
}
.agm-doc-item:last-child { border-bottom: none; }
.agm-doc-name {
  font-size: 13px;
}
.agm-doc-code {
  font-size: 10px; color: #999; margin-left: auto;
}
/* hasDividend=No 등으로 생성에서 제외되는 문서 — 시각적으로 비활성화 */
.agm-doc-item--skipped .agm-doc-name {
  color: #aaa;
  text-decoration: line-through;
  text-decoration-color: rgba(0, 0, 0, 0.25);
}
.agm-doc-item--skipped .agm-doc-num {
  background: #f5f5f5;
  color: #bbb;
}
.agm-doc-item--skipped :deep(.iconify) {
  opacity: 0.4;
}
.agm-doc-skip-badge {
  font-size: 10px !important;
  height: 20px !important;
}
.agm-load-prev {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  margin-bottom: 12px;
  background: linear-gradient(180deg, #fff8e1 0%, #fff3c4 100%);
  border: 1px solid #ffd54f;
  border-radius: 8px;
}
.agm-load-prev-text {
  flex: 1;
  font-size: 12px;
  color: #5d4037;
  font-weight: 500;
}
.agm-prev-list {
  background: transparent;
}
.agm-prev-item {
  border: 1px solid #eef0fb;
  border-radius: 8px;
  margin-bottom: 6px;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}
.agm-prev-item:hover {
  background: #f5f7ff;
  border-color: #5c6bc0;
}
/* Drag & Drop overlay — 챗봇 카드를 폼 필드에 드롭 */
.agm-drop-wrap {
  position: relative;
}
.agm-drop-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
  border: 2px dashed #5c6bc0;
  border-radius: 6px;
  background: rgba(92, 107, 192, 0.05);
  cursor: copy;
  transition: background 0.15s ease;
}
.agm-drop-overlay:hover {
  background: rgba(92, 107, 192, 0.18);
  border-color: #3949ab;
}
.agm-doc-num {
  width: 20px; height: 20px; border-radius: 4px;
  background: #eef2ff; color: #5c6bc0; font-size: 10px; font-weight: 600;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.agm-progress { padding: 0 24px 8px; }
.agm-progress-text { font-size: 11px; color: #888; margin-top: 4px; text-align: center; }
.agm-progress-text--error { color: #d32f2f; font-weight: 600; }

.agm-section-error {
  border: 1px solid rgba(211, 47, 47, 0.65);
  box-shadow: 0 0 0 2px rgba(211, 47, 47, 0.08);
}

.agm-missing-list {
  margin-top: 8px;
  padding: 10px 12px;
  border: 1px solid rgba(211, 47, 47, 0.35);
  background: rgba(211, 47, 47, 0.04);
  border-radius: 10px;
}
.agm-missing-title {
  font-size: 12px;
  font-weight: 700;
  color: #d32f2f;
  margin-bottom: 6px;
}
.agm-missing-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}
.agm-missing-title-row .agm-missing-title {
  margin-bottom: 0;
}
.agm-missing-items {
  margin: 0;
  padding-left: 16px;
  color: #b71c1c;
  font-size: 12px;
  line-height: 1.4;
}
.agm-footer {
  display: flex; justify-content: space-between;
  padding: 12px 24px; border-top: 1px solid #f0f0f0;
}
.agm-addr-suggest {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
}
</style>
